import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, Chip, Stack, Divider, Grid, Card, CardContent,
  Tab, Tabs, Alert, TextField, LinearProgress, IconButton, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, Checkbox, FormControlLabel,
  Switch, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButtonGroup, ToggleButton, Paper, Select, MenuItem, FormControl,
  InputLabel, Autocomplete, List, ListItem, ListItemText, useMediaQuery,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Share as ShareIcon,
  Payment as PaymentIcon,
  Search as SearchIcon,
  EmojiEvents as TrophyIcon,
  Group as GroupIcon,
  AccountTree as BracketIcon,
  Schedule as ScheduleIcon,
  Leaderboard as LeaderboardIcon,
  PersonAdd as PersonAddIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as TimeIcon,
  ViewStream as MirrorIcon,
  AccountTree as TreeIcon,
  Casino as CasinoIcon,
  FormatListNumbered as ChecklistIcon,
  CheckCircle as CheckCircleIcon,
  HighlightOff as HighlightOffIcon,
  DeleteOutline as DeleteOutlineIcon,
  Print as PrintIcon,
  Send as SendIcon,
  Visibility as PreviewIcon,
} from "@mui/icons-material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Tournament, Player, TournamentStatus, DiscountType } from "../types";
import { apiRequest } from "../config/api";
import { useOrganization } from "../contexts/OrganizationContext";
import { getMapsUrl } from "../utils/maps";
import { getBracketNames } from "../utils/bracketNames";
import { withByeCards } from "../utils/scheduleUtils";
import TournamentSchedulePanel from "../components/TournamentSchedulePanel";
import TournamentReplacementDialog from "../components/TournamentReplacementDialog";
import AdvancementTieDialog, { AdvancementTie } from "../components/AdvancementTieDialog";

type PaymentWindowFormState = {
  startAt: string;
  endAt: string;
  price: string;
};

type PaymentWindowKey = "earlyBird" | "normalRegistration" | "lateRegistration";

type PaymentSettingsFormState = {
  payInCash: boolean;
  accountName: string;
  bankName: string;
  iban: string;
  bic: string;
  paypalLink: string;
  paypalQrCodeUrl: string;
  emailSubject: string;
  emailMessage: string;
  earlyBird: PaymentWindowFormState;
  normalRegistration: PaymentWindowFormState;
  lateRegistration: PaymentWindowFormState;
  refundPolicy: {
    moreThanFiveDays: string;
    betweenFiveAndTwoDays: string;
    within48Hours: string;
  };
};

type EmailRecipientMode = 'all' | 'confirmed' | 'waitlist' | 'paid' | 'unpaid' | 'specific' | 'outside';

interface EmailRecipient {
  playerId: string;
  playerName: string;
  hasEmail: boolean;
  teamName: string;
  partnerName: string;
  confirmed: boolean;
  waitlist: boolean;
  paid: boolean;
}

interface EmailSendResult {
  sent: number;
  failed: number;
  skipped: number;
  total: number;
  results: Array<{ playerId: string; playerName: string; status: 'sent' | 'failed' | 'skipped'; reason?: string }>;
}

// ─── helpers ────────────────────────────────────────────────────────────────

function getStatusColor(s: TournamentStatus): "default" | "primary" | "success" | "warning" | "error" {
  if (s === "completed") return "success";
  if (s === "cancelled") return "error";
  if (s === "registration" || s === "group-stage" || s === "knockout") return "primary";
  return "default";
}
function getStatusLabel(s: string) {
  return s.split("-").map(w => w[0].toUpperCase() + w.slice(1)).join(" ");
}

function formatOverviewDateTime(value: string | Date | null | undefined): string | null {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fillEmailTemplate(template: string, vars: Record<string, string>): string {
  return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => vars[key] ?? '');
}

function emptyPaymentWindow(): PaymentWindowFormState {
  return {
    startAt: "",
    endAt: "",
    price: "",
  };
}

function emptyRefundPolicy() {
  return {
    moreThanFiveDays: "full refund",
    betweenFiveAndTwoDays: "50% refund",
    within48Hours: "no refund",
  };
}

function toDateTimeLocalValue(value: string | Date | null | undefined): string {
  if (!value) return "";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function fromDateTimeLocalValue(value: string): string {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

function buildDefaultPaymentEmailMessage(refundPolicy: PaymentSettingsFormState["refundPolicy"]): string {
  return [
    `Hi {{playerName}},`,
    ``,
    `Your registration for {{tournamentName}} is still pending payment.`,
    `Your team registered on {{registrationDateTime}} and falls into the {{registrationWindowName}} window.`,
    `The amount to be paid is {{registrationPrice}}.`,
    ``,
    `Please transfer your payment to:`,
    `Account Name: {{accountName}}`,
    `Bank: {{bankName}}`,
    `IBAN: {{iban}}`,
    `BIC: {{bic}}`,
    ``,
    `Verwendungszweck: {{paymentReference}}`,
    ``,
    `or using`,
    ``,
    `PayPal (optional): {{paypalLink}}`,
    ``,
    `but dont forget to add the Verwendungszweck even when using Paypal as a description.`,
    ``,
    `Bear in mind we can confirm your registration only when the payment is received in the bank account i.e. delays in payment because of the bank are to be calculated by the players.`,
    ``,
    `Refund policy:`,
    `More than 5 days before the tournament: {{refundMoreThanFiveDays}}`,
    `Between 5 days and 2 days before the tournament: {{refundBetweenFiveAndTwoDays}}`,
    `Within 48 hours of the tournament: {{refundWithin48Hours}}`,
    ``,
    `Thank you!`,
  ].join("\n");
}

function upgradePaymentEmailTemplate(template: string | undefined, refundPolicy: PaymentSettingsFormState["refundPolicy"]): string {
  const baseTemplate = (template || "").trim();
  if (!baseTemplate) return buildDefaultPaymentEmailMessage(refundPolicy);

  let next = baseTemplate;
  if (!next.includes("{{registrationPrice}}") || !next.includes("{{registrationDateTime}}")) {
    const registrationLine = `Your registration for {{tournamentName}} is still pending payment.`;
    const registrationBlock = [
      registrationLine,
      `Your team registered on {{registrationDateTime}} and falls into the {{registrationWindowName}} window.`,
      `The amount to be paid is {{registrationPrice}}.`,
    ].join("\n");

    next = next.includes(registrationLine)
      ? next.replace(registrationLine, registrationBlock)
      : `${registrationBlock}\n\n${next}`;
  }

  if (!next.includes("{{refundMoreThanFiveDays}}") || !next.includes("{{refundBetweenFiveAndTwoDays}}") || !next.includes("{{refundWithin48Hours}}")) {
    const refundBlock = [
      `Refund policy:`,
      `More than 5 days before the tournament: {{refundMoreThanFiveDays}}`,
      `Between 5 days and 2 days before the tournament: {{refundBetweenFiveAndTwoDays}}`,
      `Within 48 hours of the tournament: {{refundWithin48Hours}}`,
    ].join("\n");

    if (/Refund policy:\s*[\s\S]*?(Thank you!?)/.test(next)) {
      next = next.replace(/Refund policy:\s*[\s\S]*?(Thank you!?)/, `${refundBlock}\n\n$1`);
    } else if (/Thank you!?/.test(next)) {
      next = next.replace(/Thank you!?/, `${refundBlock}\n\n$&`);
    } else {
      next = `${next}\n\n${refundBlock}`;
    }
  }

  return next;
}

function areAllGroupMatchesComplete(t: Tournament | null): boolean {
  if (!t?.groups?.length) return false;
  return t.groups.every(g => {
    if (!g.standings?.length) return false;
    const n = g.teamIds.length;
    const expected = (n * (n - 1)) / 2;
    const played = (g.standings.reduce((s, x) => s + x.matchesPlayed, 0) || 0) / 2;
    return played >= expected;
  });
}

function areAllKnockoutMatchesScored(t: Tournament): boolean {
  const mc = t.knockoutBracket?.mastersCup || (t as any).mastersCupBracket || [];
  const tc = t.knockoutBracket?.tourCup || (t as any).tourCupBracket || [];
  if (!mc.length && !tc.length) return false;
  return [...mc, ...tc].every((m: any) => m.winnerId != null || m.isCancelled);
}

function getFormatInfo(t: Tournament, stage: "group" | "knockout") {
  const fmt = stage === "knockout"
    ? t.knockoutMatchFormat || t.matchFormat
    : t.groupMatchFormat || t.matchFormat;
  switch (fmt) {
    case "best-of-7": return { label: "Bo7", target: 4 };
    case "best-of-9": return { label: "Bo9", target: 5 };
    case "best-of-11": return { label: "Bo11", target: 6 };
    default: return { label: "Bo7", target: 4 };
  }
}

// ─── bracket rendering helpers ───────────────────────────────────────────────

const BRACKET_ROUND_ORDER = ["Round 1","Round 2","Round 3","Round 4","Round 5","round-of-32","round-of-16","quarter-finals","semi-finals","final"];
const CONSOLATION_ORDER = ["consolation-semi-finals","seventh-place","fifth-place","third-place"];

function uniqueRounds(bracket: any[], consolation = false) {
  const rounds = Array.from(new Set(bracket.map((m: any) => m.round)));
  const order = consolation ? CONSOLATION_ORDER : BRACKET_ROUND_ORDER;
  return rounds.sort((a: any, b: any) => {
    const ai = order.indexOf(a), bi = order.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });
}

// ─── component ───────────────────────────────────────────────────────────────

export default function TournamentAdmin() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentOrg } = useOrganization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Helper: Get bracket names based on organization
  const getBracketNamesForOrg = () => getBracketNames(currentOrg?.slug);

  // ── core state ──────────────────────────────────────────────────────────
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [snack, setSnack] = useState("");

  // ── teams tab ───────────────────────────────────────────────────────────
  const [registerOpen, setRegisterOpen] = useState(false);
  const [regPlayer1, setRegPlayer1] = useState<Player | null>(null);
  const [regPlayer2, setRegPlayer2] = useState<Player | null>(null);
  const [regTeamName, setRegTeamName] = useState("");
  const [regSaving, setRegSaving] = useState(false);
  const [regBulkSaving, setRegBulkSaving] = useState(false);

  // ── draw animation ──────────────────────────────────────────────────────
  const [potDialogOpen, setPotDialogOpen] = useState(false);
  const [pots, setPots] = useState<{ potNumber: number; teams: { id: string; name: string; avgLevel: number }[] }[]>([]);
  const [drawAnimationOpen, setDrawAnimationOpen] = useState(false);
  const [drawGroups, setDrawGroups] = useState<{ name: string; teams: { id: string; name: string; avgLevel: number }[] }[]>([]);
  const [currentDrawStep, setCurrentDrawStep] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [draggedTeam, setDraggedTeam] = useState<{ team: { id: string; name: string; avgLevel: number }; sourceGroupIdx: number } | null>(null);
  const [checklist, setChecklist] = useState<Record<string, { showed: boolean; paid: boolean; paymentReference?: string; paymentReminderSentAt?: Date; discountType?: DiscountType; discountValue?: number; discountCode?: string }>>({});
  const [checklistSearch, setChecklistSearch] = useState("");
  const [giftsEnabled, setGiftsEnabled] = useState(false);
  const [defaultGiftDescriptions, setDefaultGiftDescriptions] = useState<string[]>([]);
  const [defaultGiftsText, setDefaultGiftsText] = useState("");
  const [giftRedemptions, setGiftRedemptions] = useState<Record<string, { giftDescription?: string; redeemed: boolean; redeemedAt?: any; redeemedByAdminName?: string }>>({});
  const [overviewData, setOverviewData] = useState<{ confirmedTeams: any[]; waitlistTeams: any[]; withdrawnTeams: any[]; pendingInvitations: any[] } | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [pendingInvitesDialogOpen, setPendingInvitesDialogOpen] = useState(false);
  const [partnerBoardDialogOpen, setPartnerBoardDialogOpen] = useState(false);
  const [partnerBoardPlayers, setPartnerBoardPlayers] = useState<any[]>([]);
  const [partnerBoardLoading, setPartnerBoardLoading] = useState(false);
  const [removingPartnerBoardEntryId, setRemovingPartnerBoardEntryId] = useState<string | null>(null);
  const [deletingRegisteredTeamId, setDeletingRegisteredTeamId] = useState<string | null>(null);
  const [deleteAllTeamsOpen, setDeleteAllTeamsOpen] = useState(false);
  const [deleteAllTeamsConfirmText, setDeleteAllTeamsConfirmText] = useState("");
  const [deletingAllTeams, setDeletingAllTeams] = useState(false);
  const [deletingOverviewEntryId, setDeletingOverviewEntryId] = useState<string | null>(null);
  const [movingTeamToWaitlistId, setMovingTeamToWaitlistId] = useState<string | null>(null);
  const [renamingTeamId, setRenamingTeamId] = useState<string | null>(null);
  const [replacementsDialogOpen, setReplacementsDialogOpen] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState<PaymentSettingsFormState>({
    payInCash: false,
    accountName: '',
    bankName: '',
    iban: '',
    bic: '',
    paypalLink: '',
    paypalQrCodeUrl: '',
    emailSubject: '',
    emailMessage: '',
    earlyBird: emptyPaymentWindow(),
    normalRegistration: emptyPaymentWindow(),
    lateRegistration: emptyPaymentWindow(),
    refundPolicy: emptyRefundPolicy(),
  });
  const [savingPaymentSettings, setSavingPaymentSettings] = useState(false);
  const [qrUploading, setQrUploading] = useState(false);
  const [sendingPaymentEmails, setSendingPaymentEmails] = useState(false);
  const [paymentRecipientMode, setPaymentRecipientMode] = useState<'all' | 'specific'>('all');
  const [selectedPaymentPlayerIds, setSelectedPaymentPlayerIds] = useState<string[]>([]);
  const [emailRoster, setEmailRoster] = useState<EmailRecipient[]>([]);
  const [outsideRoster, setOutsideRoster] = useState<EmailRecipient[]>([]);
  const [outsideSearch, setOutsideSearch] = useState("");
  const [emailRosterLoading, setEmailRosterLoading] = useState(false);
  const [emailRecipientMode, setEmailRecipientMode] = useState<EmailRecipientMode>('all');
  const [selectedEmailPlayerIds, setSelectedEmailPlayerIds] = useState<string[]>([]);
  const [emailSubject, setEmailSubject] = useState('Update on {{tournamentName}}');
  const [emailMessage, setEmailMessage] = useState('Hi {{playerName}},\n\n');
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [sendingCustomEmail, setSendingCustomEmail] = useState(false);
  const [emailSendResult, setEmailSendResult] = useState<EmailSendResult | null>(null);
  const [waitlistEmailDialogOpen, setWaitlistEmailDialogOpen] = useState(false);
  const [waitlistEmailLoading, setWaitlistEmailLoading] = useState(false);
  const [waitlistEmailSending, setWaitlistEmailSending] = useState(false);
  const [waitlistEmailTarget, setWaitlistEmailTarget] = useState<any | null>(null);
  const [waitlistEmailRecipients, setWaitlistEmailRecipients] = useState<any[]>([]);
  const [waitlistEmailSubject, setWaitlistEmailSubject] = useState('');
  const [waitlistEmailMessage, setWaitlistEmailMessage] = useState('');

  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [promoteDialogLoading, setPromoteDialogLoading] = useState(false);
  const [promoteDialogSending, setPromoteDialogSending] = useState(false);
  const [promoteDialogTarget, setPromoteDialogTarget] = useState<any | null>(null);
  const [promoteDialogRecipients, setPromoteDialogRecipients] = useState<any[]>([]);
  const [promoteDialogSubject, setPromoteDialogSubject] = useState('');
  const [promoteDialogMessage, setPromoteDialogMessage] = useState('');

  // ── schedule tab ────────────────────────────────────────────────────────
  const [generating, setGenerating] = useState(false);
  const [resettingGroups, setResettingGroups] = useState(false);

  // ── scores tab ──────────────────────────────────────────────────────────
  const [matchScores, setMatchScores] = useState<Record<string, { team1Games: number; team2Games: number }>>({});
  const [editingScores, setEditingScores] = useState<Set<string>>(new Set());
  const [slotOverrides, setSlotOverrides] = useState<Map<string, boolean>>(new Map());
  const [randomDialog, setRandomDialog] = useState(false);
  const [randomRunning, setRandomRunning] = useState(false);

  // ── schedule tab – match list / delete preview ──────────────────────────
  const [matchListOpen, setMatchListOpen] = useState(false);
  const [markedForRemoval, setMarkedForRemoval] = useState<Set<string>>(new Set());

  // ── bracket tab ─────────────────────────────────────────────────────────
  const [bracketMode, setBracketMode] = useState<"mirror" | "tree">("mirror");
  const [generatingBracket, setGeneratingBracket] = useState(false);
  const [advancementTies, setAdvancementTies] = useState<AdvancementTie[] | null>(null);
  const [bracketScoreMatch, setBracketScoreMatch] = useState<{ match: any; bracketKey: "mastersCup" | "tourCup" } | null>(null);
  const [bracketScoreValues, setBracketScoreValues] = useState({ team1Games: 0, team2Games: 0 });
  const [bracketScoreSubmitting, setBracketScoreSubmitting] = useState(false);

  // ── results tab ─────────────────────────────────────────────────────────
  const [rankings, setRankings] = useState<any>(null);
  const [rankingsLoading, setRankingsLoading] = useState(false);

  // ── live display ────────────────────────────────────────────────────────
  const [displayModeDialogOpen, setDisplayModeDialogOpen] = useState(false);
  const [slideTimeDialogOpen, setSlideTimeDialogOpen] = useState(false);
  const SLIDE_DEFS = [
    { key: "overview", label: "Overview" },
    { key: "schedule", label: "Schedule" },
    { key: "upcoming", label: "Upcoming Matches" },
    { key: "results", label: "Recent Results" },
    { key: "standings", label: "Group Standings" },
    { key: "brackets-primary", label: `Knockout – ${getBracketNames(currentOrg?.slug).primary}` },
    { key: "brackets-secondary", label: `Knockout – ${getBracketNames(currentOrg?.slug).secondary}` },
  ].filter((s) => s.key !== "standings" || tournament?.format !== "single-elimination");
  const defaultSlideConfig = () =>
    Object.fromEntries(
      SLIDE_DEFS.map((s) => [s.key, { enabled: true, duration: 15 }]),
    );
  const [slideConfig, setSlideConfig] = useState<
    Record<string, { enabled: boolean; duration: number }>
  >(defaultSlideConfig());

  // ─── data fetching ───────────────────────────────────────────────────────
  const fetchRegistrationsOverview = useCallback(async () => {
    if (!tournamentId) return;
    setOverviewLoading(true);
    try {
      const res = await apiRequest(`/api/tournaments/${tournamentId}/registrations-overview`);
      if (res.ok) {
        setOverviewData(await res.json());
      } else {
        setOverviewData(null);
      }
    } catch (e) {
      console.error(e);
      setOverviewData(null);
    } finally {
      setOverviewLoading(false);
    }
  }, [tournamentId]);

  const fetchPartnerBoardPlayers = useCallback(async () => {
    if (!tournamentId) return;
    setPartnerBoardLoading(true);
    try {
      const res = await apiRequest(`/api/tournaments/${tournamentId}/partner-board`);
      if (res.ok) {
        const data = await res.json();
        setPartnerBoardPlayers(Array.isArray(data) ? data : []);
      } else {
        setPartnerBoardPlayers([]);
      }
    } catch (e) {
      console.error(e);
      setPartnerBoardPlayers([]);
    } finally {
      setPartnerBoardLoading(false);
    }
  }, [tournamentId]);

  const fetchTournament = useCallback(async () => {
    try {
      const r = await apiRequest(`/api/tournaments/${tournamentId}`);
      const data = await r.json();
      setTournament(data);
      setChecklist(data.playerChecklist || {});
      setGiftsEnabled(!!data.giftsEnabled);
      const parsedGiftDefaults = Array.isArray(data.defaultGiftDescriptions)
        ? data.defaultGiftDescriptions.filter((gift: any) => typeof gift === "string" && gift.trim().length > 0).map((gift: string) => gift.trim())
        : [];
      const normalizedGiftDefaults = parsedGiftDefaults.length > 0
        ? parsedGiftDefaults
        : (data.defaultGiftDescription ? [data.defaultGiftDescription] : []);
      setDefaultGiftDescriptions(normalizedGiftDefaults);
      setDefaultGiftsText(normalizedGiftDefaults.join("\n"));
      setGiftRedemptions(data.giftRedemptions || {});
      const refundPolicy = {
        moreThanFiveDays: data.paymentSettings?.refundPolicy?.moreThanFiveDays || "full refund",
        betweenFiveAndTwoDays: data.paymentSettings?.refundPolicy?.betweenFiveAndTwoDays || "50% refund",
        within48Hours: data.paymentSettings?.refundPolicy?.within48Hours || "no refund",
      };
      const DEFAULT_EMAIL_MESSAGE = buildDefaultPaymentEmailMessage(refundPolicy);
      setPaymentSettings({
        payInCash: !!data.paymentSettings?.payInCash,
        accountName: data.paymentSettings?.accountName || '',
        bankName: data.paymentSettings?.bankName || '',
        iban: data.paymentSettings?.iban || '',
        bic: data.paymentSettings?.bic || '',
        paypalLink: data.paymentSettings?.paypalLink || '',
        paypalQrCodeUrl: data.paymentSettings?.paypalQrCodeUrl || '',
        emailSubject: data.paymentSettings?.emailSubject || `Payment reminder for {{tournamentName}}`,
        emailMessage: upgradePaymentEmailTemplate(data.paymentSettings?.emailMessage || DEFAULT_EMAIL_MESSAGE, refundPolicy),
        earlyBird: {
          startAt: toDateTimeLocalValue(data.paymentSettings?.earlyBird?.startAt),
          endAt: toDateTimeLocalValue(data.paymentSettings?.earlyBird?.endAt),
          price: data.paymentSettings?.earlyBird?.price != null ? String(data.paymentSettings.earlyBird.price) : '',
        },
        normalRegistration: {
          startAt: toDateTimeLocalValue(data.paymentSettings?.normalRegistration?.startAt),
          endAt: toDateTimeLocalValue(data.paymentSettings?.normalRegistration?.endAt),
          price: data.paymentSettings?.normalRegistration?.price != null ? String(data.paymentSettings.normalRegistration.price) : '',
        },
        lateRegistration: {
          startAt: toDateTimeLocalValue(data.paymentSettings?.lateRegistration?.startAt),
          endAt: toDateTimeLocalValue(data.paymentSettings?.lateRegistration?.endAt),
          price: data.paymentSettings?.lateRegistration?.price != null ? String(data.paymentSettings.lateRegistration.price) : '',
        },
        refundPolicy,
      });
      fetchRegistrationsOverview();
      fetchPartnerBoardPlayers();
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tournamentId, fetchRegistrationsOverview, fetchPartnerBoardPlayers]);

  const fetchPlayers = useCallback(async () => {
    try {
      const r = await apiRequest("/api/players");
      const data = await r.json();
      setPlayers(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchTournament();
    fetchPlayers();
  }, [fetchTournament, fetchPlayers]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    const map: Record<string, number> = {
      overview: 0,
      payment: 1,
      email: 2,
      gifts: 3,
      teams: 4,
      playerinfo: 5,
      groups: 6,
      schedule: 7,
      scores: 8,
      bracket: 9,
      results: 10,
    };
    if (tabParam && map[tabParam] !== undefined) {
      setTab(map[tabParam]);
    }
  }, [location.search]);

  const msg = (m: string) => setSnack(m);

  const handleAdminRemovePartnerBoardEntry = async (entry: any) => {
    if (!tournamentId || !entry?.id) return;

    const playerName = entry.playerName || 'this player';
    const confirmed = window.confirm(
      `Remove ${playerName} from the Partner Board? This should be used for moderation (e.g. inappropriate content).`
    );
    if (!confirmed) return;

    setRemovingPartnerBoardEntryId(entry.id);
    try {
      const res = await apiRequest(`/api/tournaments/${tournamentId}/partner-board/${entry.id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        let errorMessage = 'Failed to remove partner board entry';
        try {
          const data = await res.json();
          errorMessage = data.error || errorMessage;
        } catch {
          // Ignore JSON parse errors; keep fallback message
        }
        throw new Error(errorMessage);
      }

      msg(`Removed ${playerName} from partner board.`);
      fetchPartnerBoardPlayers();
    } catch (error: any) {
      msg(error.message || 'Failed to remove partner board entry');
    } finally {
      setRemovingPartnerBoardEntryId(null);
    }
  };

  // ─── handlers ────────────────────────────────────────────────────────────

  const handleToggleRegistration = async () => {
    if (!tournament) return;
    const newStatus = tournament.status === "registration" ? "upcoming" : "registration";
    try {
      const r = await apiRequest(`/api/tournaments/${tournament.id}`, { method: "PUT", body: JSON.stringify({ status: newStatus, registrationAutoManaged: false }) });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || `Request failed (${r.status})`);
      }
      setTournament((prev: any) => (prev ? { ...prev, status: newStatus } : prev));
      msg(newStatus === "registration" ? "Registration opened" : "Registration closed");
      fetchTournament();
    } catch (e: any) {
      msg(e.message || "Failed to update registration status");
    }
  };

  const handleRegisterTeam = async () => {
    if (!tournament || !regPlayer1 || !regPlayer2) return;
    setRegSaving(true);
    try {
      const r = await apiRequest(`/api/tournaments/${tournament.id}/register`, {
        method: "POST",
        body: JSON.stringify({ player1Id: regPlayer1.id, player2Id: regPlayer2.id, teamName: regTeamName || undefined }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      msg("Team registered!");
      setRegOpen(false);
      setRegPlayer1(null); setRegPlayer2(null); setRegTeamName("");
      fetchTournament();
    } catch (e: any) { msg(e.message || "Failed to register"); }
    finally { setRegSaving(false); }
  };

  const handleAddByeTeam = async () => {
    if (!tournament) return;
    setRegSaving(true);
    try {
      const r = await apiRequest(`/api/tournaments/${tournament.id}/register`, {
        method: "POST",
        body: JSON.stringify({ isBye: true }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      msg("Bye team added!");
      setRegOpen(false);
      fetchTournament();
    } catch (e: any) { msg(e.message || "Failed to add Bye team"); }
    finally { setRegSaving(false); }
  };

  const handleFillTournamentWithTestTeams = async () => {
    if (!tournament) return;

    const registeredTeams = tournament.registeredTeams || [];
    const remainingSpots = Math.max(0, (tournament.maxTeams || 0) - registeredTeams.length);
    if (remainingSpots <= 0) {
      msg("Tournament is already full.");
      return;
    }

    // Virtual "test_" teams are created by the backend inside this tournament only;
    // no player records are created.
    setRegBulkSaving(true);
    try {
      const response = await apiRequest(`/api/tournaments/${tournament.id}/test-teams`, {
        method: "POST",
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || "Failed to add test teams");

      msg(`Added ${data.added} test team${data.added === 1 ? "" : "s"}.`);
      setRegOpen(false);
      fetchTournament();
    } catch (e: any) {
      msg(e.message || "Failed to auto-fill with test teams");
    } finally {
      setRegBulkSaving(false);
    }
  };

  const handleDeleteRegisteredTeam = async (team: any) => {
    if (!tournament || !team?.id) return;

    const teamName = team.teamName || `${team.player1Name || 'Player 1'} & ${team.player2Name || 'Player 2'}`;
    if (!window.confirm(`Delete registered team \"${teamName}\"? This cannot be undone.`)) {
      return;
    }

    setDeletingRegisteredTeamId(team.id);
    try {
      const response = await apiRequest(`/api/tournaments/${tournament.id}/registered-teams/${encodeURIComponent(team.id)}`, {
        method: 'DELETE',
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete registered team');
      }

      msg(data.resetGeneratedData ? 'Registered team deleted. Groups and knockout schedule were reset.' : 'Registered team deleted');
      fetchTournament();
      fetchRegistrationsOverview();
    } catch (e: any) {
      msg(e.message || 'Failed to delete registered team');
    } finally {
      setDeletingRegisteredTeamId(null);
    }
  };

  const handleDeleteAllRegisteredTeams = async () => {
    if (!tournament) return;

    setDeletingAllTeams(true);
    try {
      const response = await apiRequest(`/api/tournaments/${tournament.id}/registered-teams`, {
        method: 'DELETE',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete registered teams');
      }

      msg(data.resetGeneratedData ? `Deleted ${data.deleted} teams. Groups and knockout schedule were reset.` : `Deleted ${data.deleted} teams`);
      setDeleteAllTeamsOpen(false);
      setDeleteAllTeamsConfirmText("");
      fetchTournament();
      fetchRegistrationsOverview();
    } catch (e: any) {
      msg(e.message || 'Failed to delete registered teams');
    } finally {
      setDeletingAllTeams(false);
    }
  };

  const handleDeleteOverviewEntry = async (entry: any, sectionLabel: string) => {
    if (!tournament || !entry?.id) return;

    const teamName = entry.teamName || `${entry.player1?.name || 'Player 1'} & ${entry.player2?.name || 'Player 2'}`;
    if (!window.confirm(`Delete ${sectionLabel} entry \"${teamName}\"? This cannot be undone.`)) {
      return;
    }

    setDeletingOverviewEntryId(entry.id);
    try {
      const response = await apiRequest(`/api/tournaments/${tournament.id}/registrations-overview/${encodeURIComponent(entry.id)}`, {
        method: 'DELETE',
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete overview entry');
      }

      msg('Overview entry deleted');
      fetchTournament();
      fetchRegistrationsOverview();
    } catch (e: any) {
      msg(e.message || 'Failed to delete overview entry');
    } finally {
      setDeletingOverviewEntryId(null);
    }
  };

  const handleMoveTeamToWaitlist = async (team: any, sectionLabel: string) => {
    if (!tournament || !team?.id) return;

    const teamName = team.teamName || `${team.player1?.name || team.player1Name || 'Player 1'} & ${team.player2?.name || team.player2Name || 'Player 2'}`;
    if (!window.confirm(`Move ${sectionLabel} \"${teamName}\" to waitlist?`)) {
      return;
    }

    setMovingTeamToWaitlistId(team.id);
    try {
      const response = await apiRequest(`/api/tournaments/${tournament.id}/registered-teams/${encodeURIComponent(team.id)}/move-to-waitlist`, {
        method: 'POST',
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to move team to waitlist');
      }

      msg('Team moved to waitlist');
      fetchTournament();
      fetchRegistrationsOverview();
    } catch (e: any) {
      msg(e.message || 'Failed to move team to waitlist');
    } finally {
      setMovingTeamToWaitlistId(null);
    }
  };

  const handleEditTeamName = async (team: any, sectionLabel: string) => {
    if (!tournament || !team?.id) return;

    const currentName = team.teamName || `${team.player1?.name || team.player1Name || 'Player 1'} & ${team.player2?.name || team.player2Name || 'Player 2'}`;
    const nextName = window.prompt(`Edit ${sectionLabel} name`, currentName);
    if (nextName == null) return;

    const trimmed = nextName.trim();
    if (!trimmed) {
      msg('Team name cannot be empty');
      return;
    }

    if (trimmed === currentName.trim()) return;

    setRenamingTeamId(team.id);
    try {
      const response = await apiRequest(`/api/tournaments/${tournament.id}/teams/${encodeURIComponent(team.id)}/name`, {
        method: 'PATCH',
        body: JSON.stringify({ teamName: trimmed }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update team name');
      }

      msg('Team name updated');
      fetchTournament();
      fetchRegistrationsOverview();
    } catch (e: any) {
      msg(e.message || 'Failed to update team name');
    } finally {
      setRenamingTeamId(null);
    }
  };

  const setRegOpen = (v: boolean) => setRegisterOpen(v);

  const handleGenerateGroups = () => {
    if (!tournament) return;
    generatePots(tournament);
    setPotDialogOpen(true);
  };

  const generatePots = (t: Tournament) => {
    const teams = t.registeredTeams || [];
    const teamsPerGroup = t.teamsPerGroup || 4;
    const numGroups = Math.ceil(teams.length / teamsPerGroup);
    const numPots = teamsPerGroup;

    const teamsWithLevel = teams.map((team) => {
      const player1 = players.find((p) => p.id === team.player1Id);
      const player2 = players.find((p) => p.id === team.player2Id);
      const level1 = player1?.level || 0;
      const level2 = player2?.level || 0;
      const avgLevel = (level1 + level2) / 2;
      const player1Name = team.player1Name || player1?.name || "Player 1";
      const player2Name = team.player2Name || player2?.name || "Player 2";
      const name = team.teamName || `${player1Name} & ${player2Name}`;
      return { id: team.id, name, avgLevel };
    });

    teamsWithLevel.sort((a, b) => b.avgLevel - a.avgLevel);

    const newPots: { potNumber: number; teams: { id: string; name: string; avgLevel: number }[] }[] = [];
    for (let i = 0; i < numPots; i++) {
      newPots.push({ potNumber: i + 1, teams: [] });
    }

    teamsWithLevel.forEach((team, index) => {
      const potIndex = Math.floor(index / numGroups);
      if (potIndex < numPots) {
        newPots[potIndex].teams.push(team);
      }
    });

    setPots(newPots);
  };

  const moveTeamBetweenPots = (teamId: string, fromPot: number, toPot: number) => {
    const newPots = [...pots];
    const fromIdx = newPots.findIndex((p) => p.potNumber === fromPot);
    const toIdx = newPots.findIndex((p) => p.potNumber === toPot);
    const teamIndex = newPots[fromIdx].teams.findIndex((t) => t.id === teamId);
    const team = newPots[fromIdx].teams[teamIndex];
    newPots[fromIdx].teams.splice(teamIndex, 1);
    newPots[toIdx].teams.push(team);
    setPots(newPots);
  };

  const startDraw = () => {
    const numGroups = pots[0]?.teams.length || 0;
    const groups: { name: string; teams: { id: string; name: string; avgLevel: number }[] }[] = [];
    for (let i = 0; i < numGroups; i++) {
      groups.push({ name: `Group ${String.fromCharCode(65 + i)}`, teams: [] });
    }
    setDrawGroups(groups);
    setCurrentDrawStep(0);
    setPotDialogOpen(false);
    setDrawAnimationOpen(true);
    setIsDrawing(true);
  };

  const performDrawStep = useCallback(() => {
    const numGroups = drawGroups.length;
    const totalSteps = pots.length * numGroups;

    if (currentDrawStep >= totalSteps) {
      setIsDrawing(false);
      return;
    }

    const currentPot = Math.floor(currentDrawStep / numGroups);
    const currentGroup = currentDrawStep % numGroups;

    const availableTeams = pots[currentPot].teams.filter(
      (team) => !drawGroups.some((group) => group.teams.some((t) => t.id === team.id)),
    );

    if (availableTeams.length > 0) {
      const randomIndex = Math.floor(Math.random() * availableTeams.length);
      const selectedTeam = availableTeams[randomIndex];
      const newGroups = [...drawGroups];
      newGroups[currentGroup].teams.push(selectedTeam);
      setDrawGroups(newGroups);
    }

    setCurrentDrawStep((prev) => prev + 1);
  }, [currentDrawStep, drawGroups, pots]);

  const skipAnimation = () => {
    const numGroups = drawGroups.length;
    const newGroups = drawGroups.map(g => ({ ...g, teams: [] as typeof g.teams }));
    const usedTeamIds = new Set<string>();
    const totalSteps = pots.length * numGroups;
    for (let step = 0; step < totalSteps; step++) {
      const currentPot = Math.floor(step / numGroups);
      const currentGroup = step % numGroups;
      const available = pots[currentPot].teams.filter(t => !usedTeamIds.has(t.id));
      if (available.length > 0) {
        const selected = available[Math.floor(Math.random() * available.length)];
        newGroups[currentGroup].teams.push(selected);
        usedTeamIds.add(selected.id);
      }
    }
    setDrawGroups(newGroups);
    setCurrentDrawStep(totalSteps);
    setIsDrawing(false);
  };

  useEffect(() => {
    if (isDrawing && drawAnimationOpen) {
      const timer = setTimeout(() => {
        performDrawStep();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [currentDrawStep, isDrawing, drawAnimationOpen, performDrawStep]);

  const handleDrawDragStart = (team: { id: string; name: string; avgLevel: number }, groupIdx: number) => {
    setDraggedTeam({ team, sourceGroupIdx: groupIdx });
  };

  const handleDrawDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrawDrop = (targetGroupIdx: number) => {
    if (!draggedTeam) return;
    const { team, sourceGroupIdx } = draggedTeam;
    if (sourceGroupIdx === targetGroupIdx) { setDraggedTeam(null); return; }
    const newGroups = [...drawGroups];
    newGroups[sourceGroupIdx] = { ...newGroups[sourceGroupIdx], teams: newGroups[sourceGroupIdx].teams.filter((t) => t.id !== team.id) };
    newGroups[targetGroupIdx] = { ...newGroups[targetGroupIdx], teams: [...newGroups[targetGroupIdx].teams, team] };
    setDrawGroups(newGroups);
    setDraggedTeam(null);
  };

  const finalizeDrawGroups = async () => {
    if (!tournament) return;
    setGenerating(true);
    try {
      const groups = drawGroups.map((group) => ({
        id: group.name.toLowerCase().replace(" ", "-"),
        name: group.name,
        teamIds: group.teams.map((t) => t.id),
      }));
      const r = await apiRequest(`/api/tournaments/${tournament.id}/generate-groups`, {
        method: "POST",
        body: JSON.stringify({ groups }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      msg("Groups generated!");
      setDrawAnimationOpen(false);
      fetchTournament();
    } catch (e: any) { msg(e.message || "Failed to generate groups"); }
    finally { setGenerating(false); }
  };

  const handleResetGroups = async (force = false) => {
    if (!tournament) return;
    const confirmMsg = force
      ? "FORCE RESET: This will permanently delete all groups, the full schedule, AND the knockout bracket. Scores entered so far will also be lost. This cannot be undone.\n\nAre you sure?"
      : "Reset all generated groups and move the tournament back before the group stage?";
    if (!window.confirm(confirmMsg)) return;

    setResettingGroups(true);
    try {
      const r = await apiRequest(`/api/tournaments/${tournament.id}/reset-groups`, {
        method: "POST",
        body: force ? JSON.stringify({ force: true }) : undefined,
      });
      const d = await r.json();
      if (!r.ok) {
        if (d.hasActivity && !force) {
          const doForce = window.confirm(
            `This tournament already has a schedule or knockout bracket.\n\nDo you want to force-reset and delete the groups, schedule, and knockout bracket?\n\nThis cannot be undone.`
          );
          if (doForce) {
            setResettingGroups(false);
            handleResetGroups(true);
          }
          return;
        }
        throw new Error(d.error);
      }
      msg("Tournament reset to registration stage.");
      fetchTournament();
    } catch (e: any) {
      msg(e.message || "Failed to reset groups");
    } finally {
      setResettingGroups(false);
    }
  };

  const handleGenerateKnockout = async (manualSelections?: { [key: string]: string[] }) => {
    if (!tournament) return;
    setGeneratingBracket(true);
    try {
      const r = await apiRequest(`/api/tournaments/${tournament.id}/generate-knockout`, {
        method: "POST",
        ...(manualSelections ? { body: JSON.stringify({ manualSelections }) } : {}),
      });
      if (!r.ok) {
        const d = await r.json();
        if (d.error === "ADVANCEMENT_TIE") {
          setAdvancementTies(d.ties);
          return;
        }
        throw new Error(d.error);
      }
      setAdvancementTies(null);
      msg("Knockout bracket generated!");
      setTimeout(fetchTournament, 500);
    } catch (e: any) { msg(e.message || "Failed to generate bracket"); }
    finally { setGeneratingBracket(false); }
  };

  // Direct bracket generation for single-elimination tournaments (no group stage).
  const handleGenerateBracket = async () => {
    if (!tournament) return;
    setGeneratingBracket(true);
    try {
      // Seed by average player level (desc) so the strongest teams get the byes.
      const teamsWithLevel = (tournament.registeredTeams || []).map((team) => {
        const player1 = players.find((p) => p.id === team.player1Id);
        const player2 = players.find((p) => p.id === team.player2Id);
        const avgLevel = ((player1?.level || 0) + (player2?.level || 0)) / 2;
        return { id: team.id, avgLevel };
      });
      teamsWithLevel.sort((a, b) => b.avgLevel - a.avgLevel);

      const r = await apiRequest(`/api/tournaments/${tournament.id}/generate-bracket`, {
        method: "POST",
        body: JSON.stringify({ teamIds: teamsWithLevel.map((t) => t.id) }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      msg("Bracket generated!");
      setTimeout(fetchTournament, 500);
    } catch (e: any) { msg(e.message || "Failed to generate bracket"); }
    finally { setGeneratingBracket(false); }
  };

  const handleResetBracket = async (force = false) => {
    if (!tournament) return;
    const confirmMsg = force
      ? "FORCE RESET: This will permanently delete the bracket. Scores entered so far will also be lost. This cannot be undone.\n\nAre you sure?"
      : "Reset the knockout bracket and move the tournament back before the knockout stage?";
    if (!window.confirm(confirmMsg)) return;

    setResettingGroups(true);
    try {
      const r = await apiRequest(`/api/tournaments/${tournament.id}/reset-bracket`, {
        method: "POST",
        body: force ? JSON.stringify({ force: true }) : undefined,
      });
      const d = await r.json();
      if (!r.ok) {
        if (d.hasActivity && !force) {
          const doForce = window.confirm(
            `This bracket already has a schedule or scores.\n\nDo you want to force-reset and delete it?\n\nThis cannot be undone.`
          );
          if (doForce) {
            setResettingGroups(false);
            handleResetBracket(true);
          }
          return;
        }
        throw new Error(d.error);
      }
      msg("Tournament reset to registration stage.");
      fetchTournament();
    } catch (e: any) {
      msg(e.message || "Failed to reset bracket");
    } finally {
      setResettingGroups(false);
    }
  };

  const handleCompleteTournament = async () => {
    if (!tournament) return;
    try {
      const r = await apiRequest(`/api/tournaments/${tournament.id}/complete`, { method: "POST" });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      msg("Tournament completed!");
      fetchTournament();
    } catch (e: any) { msg(e.message || "Failed to complete"); }
  };

  const handleOpenLiveDisplay = (mode: "slideshow" | "scroll" | "tabs") => {
    if (!tournament) return;
    if (mode === "slideshow") {
      setDisplayModeDialogOpen(false);
      setSlideTimeDialogOpen(true);
      return;
    }
    window.open(`/display/${tournament.id}?mode=${mode}`, "_blank");
    setDisplayModeDialogOpen(false);
  };

  const handleOpenSlideshow = () => {
    if (!tournament) return;
    const enabledSlides = SLIDE_DEFS.filter((s) => slideConfig[s.key]?.enabled)
      .map((s) => `${s.key}:${slideConfig[s.key].duration}`)
      .join(",");
    if (!enabledSlides) return;
    window.open(
      `/display/${tournament.id}?mode=slideshow&slides=${enabledSlides}`,
      "_blank",
    );
    setSlideTimeDialogOpen(false);
    setSlideConfig(defaultSlideConfig());
  };

  const handleChecklistToggle = async (playerId: string, field: "showed" | "paid", value: boolean) => {
    if (!tournament) return;
    const current = checklist[playerId] || { showed: false, paid: false };
    const updated = { ...checklist, [playerId]: { ...current, [field]: value } };
    setChecklist(updated);
    try {
      await apiRequest(`/api/tournaments/${tournament.id}`, { method: "PUT", body: JSON.stringify({ playerChecklist: updated }) });
    } catch { setChecklist(checklist); }
  };

  const handleGiftsEnabledToggle = async (value: boolean) => {
    if (!tournament) return;
    setGiftsEnabled(value);
    try {
      await apiRequest(`/api/tournaments/${tournament.id}`, { method: "PUT", body: JSON.stringify({ giftsEnabled: value }) });
    } catch { setGiftsEnabled(!value); }
  };

  const handleDefaultGiftDescriptionsBlur = async () => {
    if (!tournament) return;
    const normalized = Array.from(
      new Set(
        defaultGiftsText
          .split(/\r?\n|,/)
          .map((gift) => gift.trim())
          .filter((gift) => gift.length > 0),
      ),
    );
    setDefaultGiftDescriptions(normalized);
    setDefaultGiftsText(normalized.join("\n"));
    try {
      await apiRequest(`/api/tournaments/${tournament.id}`, {
        method: "PUT",
        body: JSON.stringify({
          defaultGiftDescriptions: normalized,
          defaultGiftDescription: normalized[0] || "",
        }),
      });
    } catch { /* keep the typed value locally even if the save failed */ }
  };

  const handleGiftOverrideChange = (playerId: string, giftDescription: string) => {
    const current = giftRedemptions[playerId] || { redeemed: false };
    setGiftRedemptions({ ...giftRedemptions, [playerId]: { ...current, giftDescription } });
  };

  const handleGiftOverrideBlur = async (playerId: string) => {
    if (!tournament) return;
    try {
      await apiRequest(`/api/tournaments/${tournament.id}`, { method: "PUT", body: JSON.stringify({ giftRedemptions }) });
    } catch { /* keep the typed value locally even if the save failed */ }
  };

  const handleGiftRedeemToggle = async (playerId: string, redeemed: boolean) => {
    if (!tournament) return;
    const current = giftRedemptions[playerId] || {};
    const updated = { ...giftRedemptions, [playerId]: { ...current, redeemed, redeemedAt: redeemed ? new Date().toISOString() : undefined } };
    setGiftRedemptions(updated);
    try {
      await apiRequest(`/api/tournaments/${tournament.id}`, { method: "PUT", body: JSON.stringify({ giftRedemptions: updated }) });
    } catch { setGiftRedemptions(giftRedemptions); }
  };

  const getTournamentPlayers = () => {
    if (!tournament) return [] as Array<{ playerId: string; playerName: string; teamName: string; paid: boolean; paymentReference?: string; email?: string }>;
    const byId = new Map<string, { playerId: string; playerName: string; teamName: string; paid: boolean; paymentReference?: string; email?: string }>();
    (tournament.registeredTeams || []).forEach((team) => {
      const teamName = team.teamName || `${team.player1Name || "?"} & ${team.player2Name || "?"}`;
      const addPlayer = (playerId?: string, playerName?: string) => {
        if (!playerId || byId.has(playerId)) return;
        const state = checklist[playerId] || { showed: false, paid: false };
        const player = players.find((p) => p.id === playerId);
        byId.set(playerId, {
          playerId,
          playerName: playerName || player?.name || playerId,
          teamName,
          paid: !!state.paid,
          paymentReference: state.paymentReference,
          email: player?.email,
        });
      };
      addPlayer(team.player1Id, team.player1Name);
      addPlayer(team.player2Id, team.player2Name);
    });
    return Array.from(byId.values()).sort((a, b) => a.playerName.localeCompare(b.playerName));
  };

  const handleSavePaymentSettings = async () => {
    if (!tournament) return;
    setSavingPaymentSettings(true);
    try {
      const paymentSettingsPayload = {
        accountName: paymentSettings.accountName,
        bankName: paymentSettings.bankName,
        iban: paymentSettings.iban,
        bic: paymentSettings.bic,
        paypalLink: paymentSettings.paypalLink,
        paypalQrCodeUrl: paymentSettings.paypalQrCodeUrl,
        emailSubject: paymentSettings.emailSubject,
        emailMessage: paymentSettings.emailMessage,
        earlyBird: {
          startAt: fromDateTimeLocalValue(paymentSettings.earlyBird.startAt),
          endAt: fromDateTimeLocalValue(paymentSettings.earlyBird.endAt),
          price: paymentSettings.earlyBird.price === "" ? null : Number(paymentSettings.earlyBird.price),
        },
        normalRegistration: {
          startAt: fromDateTimeLocalValue(paymentSettings.normalRegistration.startAt),
          endAt: fromDateTimeLocalValue(paymentSettings.normalRegistration.endAt),
          price: paymentSettings.normalRegistration.price === "" ? null : Number(paymentSettings.normalRegistration.price),
        },
        lateRegistration: {
          startAt: fromDateTimeLocalValue(paymentSettings.lateRegistration.startAt),
          endAt: fromDateTimeLocalValue(paymentSettings.lateRegistration.endAt),
          price: paymentSettings.lateRegistration.price === "" ? null : Number(paymentSettings.lateRegistration.price),
        },
        refundPolicy: paymentSettings.refundPolicy,
        payInCash: paymentSettings.payInCash,
      };

      const response = await apiRequest(`/api/tournaments/${tournament.id}`, {
        method: "PUT",
        body: JSON.stringify({ paymentSettings: paymentSettingsPayload }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to save payment settings");
      }
      msg("Payment settings saved");
      fetchTournament();
    } catch (e: any) {
      msg(e.message || "Failed to save payment settings");
    } finally {
      setSavingPaymentSettings(false);
    }
  };

  const handleQrUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !tournament) return;
    setQrUploading(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });
      const response = await apiRequest("/api/tournaments/upload-paypal-qr", {
        method: "POST",
        body: JSON.stringify({ fileBase64: dataUrl, contentType: file.type || "image/png" }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Upload failed");
      }
      const { url } = await response.json();
      setPaymentSettings((prev) => ({ ...prev, paypalQrCodeUrl: url }));
      msg("PayPal QR code uploaded");
    } catch (err: any) {
      msg(err.message || "Failed to upload QR code");
    } finally {
      setQrUploading(false);
      e.target.value = "";
    }
  };

  const handleQrClear = async () => {
    if (!tournament) return;
    setPaymentSettings((prev) => ({ ...prev, paypalQrCodeUrl: "" }));
    try {
      const paymentSettingsPayload = {
        accountName: paymentSettings.accountName,
        bankName: paymentSettings.bankName,
        iban: paymentSettings.iban,
        bic: paymentSettings.bic,
        paypalLink: paymentSettings.paypalLink,
        paypalQrCodeUrl: "",
        emailSubject: paymentSettings.emailSubject,
        emailMessage: paymentSettings.emailMessage,
        earlyBird: {
          startAt: fromDateTimeLocalValue(paymentSettings.earlyBird.startAt),
          endAt: fromDateTimeLocalValue(paymentSettings.earlyBird.endAt),
          price: paymentSettings.earlyBird.price === "" ? null : Number(paymentSettings.earlyBird.price),
        },
        normalRegistration: {
          startAt: fromDateTimeLocalValue(paymentSettings.normalRegistration.startAt),
          endAt: fromDateTimeLocalValue(paymentSettings.normalRegistration.endAt),
          price: paymentSettings.normalRegistration.price === "" ? null : Number(paymentSettings.normalRegistration.price),
        },
        lateRegistration: {
          startAt: fromDateTimeLocalValue(paymentSettings.lateRegistration.startAt),
          endAt: fromDateTimeLocalValue(paymentSettings.lateRegistration.endAt),
          price: paymentSettings.lateRegistration.price === "" ? null : Number(paymentSettings.lateRegistration.price),
        },
        refundPolicy: paymentSettings.refundPolicy,
        payInCash: paymentSettings.payInCash,
      };

      await apiRequest(`/api/tournaments/${tournament.id}`, {
        method: "PUT",
        body: JSON.stringify({ paymentSettings: paymentSettingsPayload }),
      });
      msg("PayPal QR code removed");
    } catch {
      msg("Failed to remove QR code");
    }
  };

  const handleSendPaymentEmails = async () => {
    if (!tournament) return;
    setSendingPaymentEmails(true);
    try {
      const paymentSettingsPayload = {
        accountName: paymentSettings.accountName,
        bankName: paymentSettings.bankName,
        iban: paymentSettings.iban,
        bic: paymentSettings.bic,
        paypalLink: paymentSettings.paypalLink,
        paypalQrCodeUrl: paymentSettings.paypalQrCodeUrl,
        emailSubject: paymentSettings.emailSubject,
        emailMessage: paymentSettings.emailMessage,
        earlyBird: {
          startAt: fromDateTimeLocalValue(paymentSettings.earlyBird.startAt),
          endAt: fromDateTimeLocalValue(paymentSettings.earlyBird.endAt),
          price: paymentSettings.earlyBird.price === "" ? null : Number(paymentSettings.earlyBird.price),
        },
        normalRegistration: {
          startAt: fromDateTimeLocalValue(paymentSettings.normalRegistration.startAt),
          endAt: fromDateTimeLocalValue(paymentSettings.normalRegistration.endAt),
          price: paymentSettings.normalRegistration.price === "" ? null : Number(paymentSettings.normalRegistration.price),
        },
        lateRegistration: {
          startAt: fromDateTimeLocalValue(paymentSettings.lateRegistration.startAt),
          endAt: fromDateTimeLocalValue(paymentSettings.lateRegistration.endAt),
          price: paymentSettings.lateRegistration.price === "" ? null : Number(paymentSettings.lateRegistration.price),
        },
        refundPolicy: paymentSettings.refundPolicy,
        payInCash: paymentSettings.payInCash,
      };

      const response = await apiRequest(`/api/tournaments/${tournament.id}/send-payment-reminders`, {
        method: "POST",
        body: JSON.stringify({
          sendTo: paymentRecipientMode,
          playerIds: paymentRecipientMode === "specific" ? selectedPaymentPlayerIds : undefined,
          ...paymentSettingsPayload,
          subject: paymentSettings.emailSubject,
          message: paymentSettings.emailMessage,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to send payment reminder emails");
      }

      const skipped = Array.isArray(data.skippedNoEmail) ? data.skippedNoEmail.length : 0;
      msg(`Sent ${data.sent || 0} payment reminder email(s)${skipped > 0 ? `, skipped ${skipped} without email` : ""}`);
      setSelectedPaymentPlayerIds([]);
      fetchTournament();
    } catch (e: any) {
      msg(e.message || "Failed to send payment reminder emails");
    } finally {
      setSendingPaymentEmails(false);
    }
  };

  const fetchEmailRoster = useCallback(async () => {
    if (!tournamentId) return;
    setEmailRosterLoading(true);
    try {
      const response = await apiRequest(`/api/tournaments/${tournamentId}/email-recipients`);
      const data = await response.json();
      if (response.ok) {
        setEmailRoster(data.recipients || []);
        setOutsideRoster(data.outsideRecipients || []);
      }
    } catch (e) {
      console.error("Failed to fetch email recipients", e);
    } finally {
      setEmailRosterLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    if (tab === 2) {
      fetchEmailRoster();
    }
  }, [tab, fetchEmailRoster]);

  const getEmailTargets = (mode: EmailRecipientMode): EmailRecipient[] => {
    if (mode === "confirmed") return emailRoster.filter((r) => r.confirmed);
    if (mode === "waitlist") return emailRoster.filter((r) => r.waitlist);
    if (mode === "paid") return emailRoster.filter((r) => r.confirmed && r.paid);
    if (mode === "unpaid") return emailRoster.filter((r) => r.confirmed && !r.paid);
    if (mode === "specific") return emailRoster.filter((r) => selectedEmailPlayerIds.includes(r.playerId));
    if (mode === "outside") return outsideRoster.filter((r) => selectedEmailPlayerIds.includes(r.playerId));
    return emailRoster;
  };

  const emailTargets = getEmailTargets(emailRecipientMode);

  const buildEmailPreview = () => {
    const sample = emailTargets[0];
    const vars = {
      playerName: sample?.playerName || "Sample Player",
      partnerName: sample?.partnerName || "Sample Partner",
      teamName: sample?.teamName || "Sample Team",
      tournamentName: tournament?.name || "the tournament",
    };
    return {
      subject: fillEmailTemplate(emailSubject, vars),
      message: fillEmailTemplate(emailMessage, vars),
      sampleName: sample?.playerName || null,
    };
  };

  const handleSendCustomEmail = async () => {
    if (!tournament) return;
    setSendingCustomEmail(true);
    try {
      const response = await apiRequest(`/api/tournaments/${tournament.id}/send-custom-email`, {
        method: "POST",
        body: JSON.stringify({
          sendTo: emailRecipientMode,
          playerIds: (emailRecipientMode === "specific" || emailRecipientMode === "outside") ? selectedEmailPlayerIds : undefined,
          subject: emailSubject,
          message: emailMessage,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to send emails");
      }

      setEmailSendResult(data);
      setEmailPreviewOpen(false);
      msg(`Sent ${data.sent || 0} email(s)${data.failed ? `, ${data.failed} failed` : ""}${data.skipped ? `, ${data.skipped} skipped` : ""}`);
    } catch (e: any) {
      msg(e.message || "Failed to send emails");
    } finally {
      setSendingCustomEmail(false);
    }
  };

  const handleOpenWaitlistEmailDialog = async (team: any) => {
    if (!tournament || !team?.id) return;
    setWaitlistEmailLoading(true);
    setWaitlistEmailTarget(team);
    try {
      const response = await apiRequest(`/api/tournaments/${tournament.id}/waitlist/${team.id}/email-preview`, {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate email preview');
      }

      setWaitlistEmailRecipients(Array.isArray(data.recipients) ? data.recipients : []);
      setWaitlistEmailSubject(data.subjectTemplate || 'Waitlist update');
      setWaitlistEmailMessage(data.messageTemplate || '');
      setWaitlistEmailDialogOpen(true);
    } catch (e: any) {
      msg(e.message || 'Failed to generate email preview');
    } finally {
      setWaitlistEmailLoading(false);
    }
  };

  const handleSendWaitlistEmail = async () => {
    if (!tournament || !waitlistEmailTarget?.id) return;
    setWaitlistEmailSending(true);
    try {
      const response = await apiRequest(`/api/tournaments/${tournament.id}/waitlist/${waitlistEmailTarget.id}/send-email`, {
        method: 'POST',
        body: JSON.stringify({
          subject: waitlistEmailSubject,
          message: waitlistEmailMessage,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send waitlist email');
      }

      const skipped = Array.isArray(data.skippedNoEmail) ? data.skippedNoEmail.length : 0;
      msg(`Sent ${data.sent || 0} waitlist email(s)${skipped > 0 ? `, skipped ${skipped} without email` : ''}`);
      setWaitlistEmailDialogOpen(false);
      fetchRegistrationsOverview();
    } catch (e: any) {
      msg(e.message || 'Failed to send waitlist email');
    } finally {
      setWaitlistEmailSending(false);
    }
  };

  const handleOpenPromoteDialog = async (team: any) => {
    if (!tournament || !team?.id) return;
    setPromoteDialogLoading(true);
    setPromoteDialogTarget(team);
    try {
      const response = await apiRequest(`/api/tournaments/${tournament.id}/waitlist/${team.id}/promote-preview`, {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to generate promotion email preview');
      setPromoteDialogRecipients(Array.isArray(data.recipients) ? data.recipients : []);
      setPromoteDialogSubject(data.subjectTemplate || '');
      setPromoteDialogMessage(data.messageTemplate || '');
      setPromoteDialogOpen(true);
    } catch (e: any) {
      msg(e.message || 'Failed to generate promotion email preview');
    } finally {
      setPromoteDialogLoading(false);
    }
  };

  const handleConfirmPromote = async () => {
    if (!tournament || !promoteDialogTarget?.id) return;
    setPromoteDialogSending(true);
    try {
      const response = await apiRequest(`/api/tournaments/${tournament.id}/waitlist/${promoteDialogTarget.id}/promote`, {
        method: 'POST',
        body: JSON.stringify({ subject: promoteDialogSubject, message: promoteDialogMessage }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to promote team');
      const skipped = Array.isArray(data.skippedNoEmail) ? data.skippedNoEmail.length : 0;
      msg(`Team promoted to registered. Sent ${data.sent || 0} email(s)${skipped > 0 ? `, skipped ${skipped} without email` : ''}.`);
      setPromoteDialogOpen(false);
      fetchRegistrationsOverview();
      fetchTournament();
    } catch (e: any) {
      msg(e.message || 'Failed to promote team');
    } finally {
      setPromoteDialogSending(false);
    }
  };

  // ─── scores tab helpers ──────────────────────────────────────────────────

  const generateRR = (teamIds: string[]) => {
    const out: { team1Id: string; team2Id: string }[] = [];
    for (let i = 0; i < teamIds.length; i++)
      for (let j = i + 1; j < teamIds.length; j++)
        out.push({ team1Id: teamIds[i], team2Id: teamIds[j] });
    return out;
  };

  const getAllGroupMatches = () => {
    if (!tournament?.groups) return [];
    const byeTeamIds = new Set((tournament.registeredTeams || []).filter((t: any) => t.isBye).map((t: any) => t.id));
    return tournament.groups.flatMap(g =>
      generateRR(g.teamIds)
        .filter(m => m.team1Id && m.team2Id && !byeTeamIds.has(m.team1Id) && !byeTeamIds.has(m.team2Id))
        .map(m => {
          const res = g.matchResults?.find(r =>
            (r.team1Id === m.team1Id && r.team2Id === m.team2Id) ||
            (r.team1Id === m.team2Id && r.team2Id === m.team1Id)
          );
          return {
            groupId: g.id, groupName: g.name, ...m,
            isScored: res ? (res.team1Games > 0 || res.team2Games > 0) : false,
            stage: "group" as const,
            startTime: res?.startTime || null,
            courtNumber: res?.courtNumber || null,
          };
        })
    );
  };

  const getAllKnockoutMatches = () => {
    if (!tournament) return [];
    const bracketNames = getBracketNamesForOrg();
    const mc = tournament.knockoutBracket?.mastersCup || (tournament as any).mastersCupBracket || [];
    const tc = tournament.knockoutBracket?.tourCup || (tournament as any).tourCupBracket || [];
    return [...mc.map((m: any) => ({ ...m, bracketType: bracketNames.primary })), ...tc.map((m: any) => ({ ...m, bracketType: bracketNames.secondary }))]
      .filter((m: any) => !m.isPlaceholder && !m.isWalkover && (m.team1Id || m.team2Id))
      .map((m: any) => ({
        matchId: m.matchId || m.id, bracketType: m.bracketType, round: m.round,
        team1Id: m.team1Id, team2Id: m.team2Id, winnerId: m.winnerId,
        team1Games: m.team1Games || 0, team2Games: m.team2Games || 0,
        isScored: !!m.winnerId || !!m.isCancelled, isCancelled: !!m.isCancelled, stage: "knockout" as const,
        startTime: m.startTime || null, courtNumber: m.courtNumber || null,
      }));
  };

  const getMatchesBySlot = (matches: any[]) => {
    const map = new Map<string, any[]>();
    matches.forEach(m => {
      const key = m.startTime ? new Date(m.startTime).toTimeString().slice(0, 5) : "unscheduled";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(m);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a === "unscheduled" ? 1 : b === "unscheduled" ? -1 : a.localeCompare(b))
      .map(([slot, ms]) => ({ slot, label: slot === "unscheduled" ? "Unscheduled" : slot, matches: ms.sort((a: any, b: any) => (a.courtNumber || 99) - (b.courtNumber || 99)) }));
  };

  const updateMatchScore = (key: string, team: 1 | 2, val: number) =>
    setMatchScores(prev => ({ ...prev, [key]: { ...(prev[key] || { team1Games: 0, team2Games: 0 }), [team === 1 ? "team1Games" : "team2Games"]: val } }));

  const startEditingScore = (key: string, current: { team1Games: number; team2Games: number }) => {
    setMatchScores(prev => ({ ...prev, [key]: { ...current } }));
    setEditingScores(prev => new Set(prev).add(key));
  };

  const cancelEditingScore = (key: string) => {
    setEditingScores(prev => { const n = new Set(prev); n.delete(key); return n; });
    setMatchScores(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const handleScoreMatch = async (match: any) => {
    if (!tournament) return;
    const key = match.stage === "knockout" ? `knockout-${match.matchId}` : `${match.groupId}-${match.team1Id}-${match.team2Id}`;
    const scores = matchScores[key];
    if (!scores || (scores.team1Games === 0 && scores.team2Games === 0)) { msg("Enter scores first"); return; }
    try {
      const endpoint = match.stage === "knockout"
        ? `/api/tournaments/${tournamentId}/score-knockout-match`
        : `/api/tournaments/${tournamentId}/score-match`;
      const bracketNames = getBracketNamesForOrg();
      const body = match.stage === "knockout"
        ? { matchId: match.matchId, team1Games: scores.team1Games, team2Games: scores.team2Games, bracketType: match.bracketType === bracketNames.primary ? "mastersCup" : "tourCup" }
        : { groupId: match.groupId, team1Id: match.team1Id, team2Id: match.team2Id, team1Games: scores.team1Games, team2Games: scores.team2Games };
      const r = await apiRequest(endpoint, { method: "POST", body: JSON.stringify(body) });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      msg("Scored!");
      setMatchScores(prev => { const n = { ...prev }; delete n[key]; return n; });
      setEditingScores(prev => { const n = new Set(prev); n.delete(key); return n; });
      fetchTournament();
    } catch (e: any) { msg(e.message || "Failed to score"); }
  };

  const openBracketScoreDialog = (match: any, bracketKey: "mastersCup" | "tourCup") => {
    if (!match.team1Id || !match.team2Id || match.isCancelled) return;
    setBracketScoreValues({ team1Games: match.team1Games || 0, team2Games: match.team2Games || 0 });
    setBracketScoreMatch({ match, bracketKey });
  };

  const handleSubmitBracketScore = async () => {
    if (!bracketScoreMatch || !tournament) return;
    const { match, bracketKey } = bracketScoreMatch;
    const { team1Games, team2Games } = bracketScoreValues;
    if (team1Games === team2Games) { msg("Knockout matches cannot end in a draw"); return; }
    setBracketScoreSubmitting(true);
    try {
      const r = await apiRequest(`/api/tournaments/${tournamentId}/score-knockout-match`, {
        method: "POST",
        body: JSON.stringify({ matchId: match.id, team1Games, team2Games, bracketType: bracketKey }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      msg("Scored!");
      setBracketScoreMatch(null);
      fetchTournament();
    } catch (e: any) { msg(e.message || "Failed to score"); }
    finally { setBracketScoreSubmitting(false); }
  };

  const handleRandomScoreAll = async () => {
    setRandomDialog(false);
    setRandomRunning(true);
    const allMatches = [...getAllGroupMatches(), ...getAllKnockoutMatches()].filter(m => !m.isScored);
    let ok = 0, fail = 0;
    for (const m of allMatches) {
      try {
        const fmt = getFormatInfo(tournament!, m.stage);
        const t = fmt.target;
        const loser = Math.floor(Math.random() * t);
        const scores = Math.random() > 0.5 ? { team1Games: t, team2Games: loser } : { team1Games: loser, team2Games: t };
        const endpoint = m.stage === "knockout"
          ? `/api/tournaments/${tournamentId}/score-knockout-match`
          : `/api/tournaments/${tournamentId}/score-match`;
        const bracketNames = getBracketNamesForOrg();
        const body = m.stage === "knockout"
          ? { matchId: m.matchId, ...scores, bracketType: m.bracketType === bracketNames.primary ? "mastersCup" : "tourCup" }
          : { groupId: m.groupId, team1Id: m.team1Id, team2Id: m.team2Id, ...scores };
        const r = await apiRequest(endpoint, { method: "POST", body: JSON.stringify(body) });
        if (r.ok) ok++; else fail++;
        await new Promise(res => setTimeout(res, 80));
      } catch { fail++; }
    }
    msg(`${ok} scored${fail ? `, ${fail} failed` : ""}.`);
    setRandomRunning(false);
    fetchTournament();
  };

  // ─── bracket rendering ────────────────────────────────────────────────────

  const getTeamName = (teamId: string) => {
    const t = tournament?.registeredTeams?.find(r => r.id === teamId);
    return t ? (t.teamName || `${t.player1Name || "P1"} / ${t.player2Name || "P2"}`) : "TBD";
  };

  const renderBracketCard = (match: any, bracketKey?: "mastersCup" | "tourCup") => {
    if (match.isByeCard) {
      return (
        <Card
          key={match.id}
          variant="outlined"
          sx={{ width: "100%", borderStyle: "dashed", bgcolor: "background.paper" }}
        >
          <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: "0.65rem" }}>
              Round 1
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "3px 6px", borderRadius: 1 }}>
              <Typography variant="body2" noWrap sx={{ flex: 1, fontSize: "0.75rem", fontWeight: 700 }}>
                {getTeamName(match.byeTeamId)}
              </Typography>
            </Box>
            <Chip label="BYE" size="small" color="default" sx={{ mt: 0.5, width: "100%", height: 16, fontSize: "0.6rem" }} />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontSize: "0.6rem" }}>
              Advances directly — no Round 1 opponent
            </Typography>
          </CardContent>
        </Card>
      );
    }
    const isPlayable = !!bracketKey && !!match.team1Id && !!match.team2Id && !match.isCancelled && !match.isWalkover;
    return (
      <Card
        key={match.id || match.matchId}
        variant="outlined"
        onClick={isPlayable ? () => openBracketScoreDialog(match, bracketKey!) : undefined}
        sx={{
          width: "100%",
          bgcolor: match.winnerId ? "success.50" : "background.paper",
          borderColor: match.winnerId ? "success.light" : "divider",
          cursor: isPlayable ? "pointer" : "default",
          transition: "box-shadow 0.15s, transform 0.15s",
          "&:hover": isPlayable ? { boxShadow: 3, transform: "translateY(-1px)" } : undefined,
        }}
      >
        <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: "0.65rem" }}>
            Match #{(match.matchNumber ?? 0) + 1}{match.courtNumber ? ` · Ct ${match.courtNumber}` : ""}
          </Typography>
          {[{ id: match.team1Id, games: match.team1Games, ph: match.team1Placeholder }, { id: match.team2Id, games: match.team2Games, ph: match.team2Placeholder }].map((side, si) => (
            <Box key={si} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "3px 6px", borderRadius: 1, bgcolor: side.id && side.id === match.winnerId ? "success.100" : "transparent", border: 1, borderColor: side.id === match.winnerId ? "success.light" : "transparent", mt: si ? 0.5 : 0 }}>
              <Typography variant="body2" noWrap sx={{ flex: 1, fontSize: "0.75rem", fontWeight: side.id === match.winnerId ? 700 : 400 }}>
                {side.id ? getTeamName(side.id) : (side.ph || "TBD")}
              </Typography>
              {side.games != null && <Typography fontWeight={700} sx={{ ml: 0.5, minWidth: 16, fontSize: "0.8rem" }}>{side.games}</Typography>}
            </Box>
          ))}
          {(match.winnerId || match.isWalkover) && <Chip label={match.isWalkover ? "Walkover (BYE)" : "✓"} size="small" color="success" sx={{ mt: 0.5, width: "100%", height: 16, fontSize: "0.6rem" }} />}
        </CardContent>
      </Card>
    );
  };

  const renderBracketSection = (bracket: any[], label: string, bracketKey: "mastersCup" | "tourCup") => {
    const mainRounds = bracket.filter((m: any) => !["consolation-semi-finals","third-place","fifth-place","seventh-place"].includes(m.round));
    const consolation = bracket.filter((m: any) => ["consolation-semi-finals","third-place","fifth-place","seventh-place"].includes(m.round));
    const renderCols = (matches: any[], consol = false) => {
      const rounds = uniqueRounds(matches, consol);
      if (bracketMode === "mirror" && !consol) {
        const finalRound = rounds[rounds.length - 1];
        const finalMatch = matches.filter(m => m.round === finalRound)[0];
        const left: any[] = [], right: any[] = [];
        rounds.slice(0, -1).forEach(r => {
          const rm = matches.filter(m => m.round === r);
          const mid = Math.ceil(rm.length / 2);
          left.push({ round: r, matches: rm.slice(0, mid) });
          right.push({ round: r, matches: rm.slice(mid) });
        });
        return (
          <Box sx={{ display: "flex", gap: 1, width: "100%", alignItems: "center" }}>
            {left.map(({ round, matches: rm }) => (
              <Box key={`l-${round}`} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" fontWeight={700} display="block" textAlign="center" mb={0.5} sx={{ fontSize: "0.65rem", textTransform: "capitalize" }}>{round.replace(/-/g, " ")}</Typography>
                <Stack spacing={1}>{rm.map(m => renderBracketCard(m, bracketKey))}</Stack>
              </Box>
            ))}
            {finalMatch && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" fontWeight={700} display="block" textAlign="center" mb={0.5} sx={{ fontSize: "0.65rem", textTransform: "capitalize" }}>{finalRound.replace(/-/g, " ")}</Typography>
                {renderBracketCard(finalMatch, bracketKey)}
              </Box>
            )}
            {right.slice().reverse().map(({ round, matches: rm }) => (
              <Box key={`r-${round}`} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" fontWeight={700} display="block" textAlign="center" mb={0.5} sx={{ fontSize: "0.65rem", textTransform: "capitalize" }}>{round.replace(/-/g, " ")}</Typography>
                <Stack spacing={1}>{rm.map(m => renderBracketCard(m, bracketKey))}</Stack>
              </Box>
            ))}
          </Box>
        );
      }
      return (
        <Box sx={{ display: "flex", gap: 1, width: "100%", alignItems: "flex-start" }}>
          {rounds.map(r => (
            <Box key={r} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" fontWeight={700} display="block" textAlign="center" mb={0.5} sx={{ fontSize: "0.65rem", textTransform: "capitalize" }}>{r.replace(/-/g, " ")}</Typography>
              <Stack spacing={1}>{matches.filter(m => m.round === r).map(m => renderBracketCard(m, bracketKey))}</Stack>
            </Box>
          ))}
        </Box>
      );
    };
    return (
      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <TrophyIcon fontSize="small" color="primary" /> {label}
        </Typography>
        {renderCols(mainRounds)}
        {consolation.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1.5 }}><Typography variant="caption" color="text.secondary">Consolation</Typography></Divider>
            {renderCols(consolation, true)}
          </Box>
        )}
      </Box>
    );
  };

  // ─── tab content ─────────────────────────────────────────────────────────

  const renderOverviewTab = () => {
    if (!tournament) return null;
    const reg = tournament.registeredTeams?.length || 0;
    const pct = Math.min(100, (reg / (tournament.maxTeams || 1)) * 100);
    const fmtLabel = (f?: string) => ({ "best-of-7": "Bo7", "best-of-9": "Bo9", "best-of-11": "Bo11", "best-of-3": "Bo3", "best-of-5": "Bo5" }[f || ""] || null);
    const gf = fmtLabel(tournament.groupMatchFormat || tournament.matchFormat);
    const kf = fmtLabel(tournament.knockoutMatchFormat || tournament.matchFormat);
    const fmtSuffix = gf && kf && gf !== kf ? ` · Groups: ${gf} / KO: ${kf}` : (gf || kf) ? ` · ${gf || kf}` : "";

    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Stack spacing={1.5}>
              {tournament.description && (
                <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>{tournament.description}</Typography>
              )}
              <Divider />
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                <Box><Typography variant="caption" color="text.secondary">📅 Date</Typography><Typography variant="body2" fontWeight={600}>{new Date(tournament.startDate).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}{tournament.startTime ? ` · ${tournament.startTime}` : ""}</Typography></Box>
                {tournament.venue && <Box><Typography variant="caption" color="text.secondary">📍 Venue</Typography><Typography variant="body2" fontWeight={600} component="a" href={getMapsUrl(tournament.venue)} target="_blank" rel="noopener noreferrer" sx={{ display: "block", color: "inherit", textDecoration: "underline" }}>{tournament.venue}</Typography></Box>}
                <Box><Typography variant="caption" color="text.secondary">Format</Typography><Typography variant="body2" fontWeight={600}>{tournament.format.split("-").map(w => w[0].toUpperCase() + w.slice(1)).join(" ")}{tournament.knockoutFormatType ? ` · ${tournament.knockoutFormatType === "double-bracket" ? "Double Bracket" : "Single Bracket"}` : ""}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Scoring</Typography><Typography variant="body2" fontWeight={600}>{tournament.scoringSystem === "sets-games" ? `Sets & Games${fmtSuffix}` : `Timed · ${tournament.timedMatchDuration || 20}min`}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Level</Typography><Chip label={tournament.level} size="small" variant="outlined" /></Box>
                {!tournament.isPointsRelevant && <Box><Chip label="One-off · No points" size="small" color="warning" variant="outlined" /></Box>}
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <Stack spacing={2}>
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>Teams registered</Typography>
                  <Typography variant="body2" fontWeight={700}>{reg} / {tournament.maxTeams}</Typography>
                </Box>
                <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 3 }} color={pct >= 100 ? "success" : "primary"} />
                <Typography variant="caption" color="text.secondary">{tournament.maxTeams - reg > 0 ? `${tournament.maxTeams - reg} spots remaining` : "Full"}</Typography>
              </Box>

              {tournament.status !== "completed" && tournament.status !== "cancelled" && tournament.status !== "group-stage" && tournament.status !== "knockout" && (
                <Tooltip title={tournament.status === "registration" ? "Close registration" : "Open registration for players"}>
                  <FormControlLabel
                    control={<Switch checked={tournament.status === "registration"} onChange={handleToggleRegistration} color="success" />}
                    label={<Typography variant="body2" fontWeight={600} color={tournament.status === "registration" ? "success.main" : "text.secondary"}>Registration {tournament.status === "registration" ? "Open 🟢" : "Closed 🔴"}</Typography>}
                  />
                </Tooltip>
              )}

              {tournament.isVisibleToPlayers === false
                ? <Chip label="Hidden from players" size="small" color="default" sx={{ width: "fit-content" }} />
                : tournament.visibleToPlayerIds?.length
                ? <Chip label={`Visible to ${tournament.visibleToPlayerIds.length} specific players`} size="small" color="warning" sx={{ width: "fit-content" }} />
                : <Chip label="Visible to all players" size="small" color="success" variant="outlined" sx={{ width: "fit-content" }} />
              }

              {((tournament as any).sponsors || []).length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Sponsors</Typography>
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                    {((tournament as any).sponsors as any[]).map((s: any) => (
                      <Box key={s.name} component="img" src={s.logoUrl} alt={s.name} sx={{ height: 28, objectFit: "contain" }} />
                    ))}
                  </Box>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => navigate("/admin/tournaments")}>Edit Tournament</Button>
                <Button size="small" variant="outlined" startIcon={<ShareIcon />} onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/join/${tournament.id}`); msg("Share link copied!"); }}>Copy Share Link</Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/player-registration?tournamentId=${tournament.id}`);
                    msg("Account signup link copied!");
                  }}
                >
                  Copy Account Signup Link
                </Button>
                <Button size="small" variant="outlined" onClick={() => setDisplayModeDialogOpen(true)}>📺 Live Display</Button>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderPlayerInfoTab = () => {
    if (!tournament) return null;
    const entries: { playerId: string; fallbackName: string; team: string }[] = [];
    const seen = new Set<string>();
    const add = (playerId: string | undefined, fallbackName: string | undefined, team: string) => {
      if (!playerId || seen.has(playerId)) return;
      seen.add(playerId);
      entries.push({ playerId, fallbackName: fallbackName || playerId, team });
    };
    (tournament.registeredTeams || []).forEach(team => {
      if ((team as any).isBye) return;
      const label = team.teamName || `${team.player1Name || "?"} & ${team.player2Name || "?"}`;
      add(team.player1Id, team.player1Name, label);
      add(team.player2Id, team.player2Name, label);
    });
    ((tournament as any).americanaPlayerIds || []).forEach((id: string) => add(id, undefined, "—"));

    const rows = entries.map(e => {
      const p = players.find(pl => pl.id === e.playerId) as any;
      const fullName = p?.name || e.fallbackName;
      const parts = String(fullName).trim().split(/s+/);
      return {
        id: e.playerId,
        name: fullName,
        firstName: p?.firstName || parts[0] || "",
        lastName: p?.lastName || parts.slice(1).join(" "),
        email: p?.email || "",
        phone: p?.phone || "",
        level: p?.level,
        team: e.team,
      };
    }).sort((a, b) => a.name.localeCompare(b.name));

    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>Player Info ({rows.length})</Typography>
        {rows.length === 0 ? (
          <Alert severity="info">No players registered yet.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Player</TableCell>
                  <TableCell>First name</TableCell>
                  <TableCell>Last name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Team</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                    <TableCell>{r.firstName || "—"}</TableCell>
                    <TableCell>{r.lastName || "—"}</TableCell>
                    <TableCell>{r.email ? <a href={`mailto:${r.email}`}>{r.email}</a> : "—"}</TableCell>
                    <TableCell>{r.phone || "—"}</TableCell>
                    <TableCell>{r.level ?? "—"}</TableCell>
                    <TableCell>{r.team}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    );
  };

  const renderTeamsTab = () => {
    if (!tournament) return null;
    const hasOpenSpots = (tournament.registeredTeams?.length || 0) < (tournament.maxTeams || 0);
    const canAdminRegisterTeam = tournament.status !== "completed" && tournament.status !== "cancelled" && hasOpenSpots;
    const byPlayerId = new Map<string, { playerId: string; playerName: string; teamName: string }>();
    (tournament.registeredTeams || []).forEach(team => {
      const label = team.teamName || `${team.player1Name || "?"} & ${team.player2Name || "?"}`;
      if (team.player1Id) {
        byPlayerId.set(team.player1Id, {
          playerId: team.player1Id,
          playerName: team.player1Name || team.player1Id,
          teamName: label,
        });
      }
      if (team.player2Id) {
        byPlayerId.set(team.player2Id, {
          playerId: team.player2Id,
          playerName: team.player2Name || team.player2Id,
          teamName: label,
        });
      }
    });

    // Keep paid players visible even if their team is no longer in registeredTeams.
    Object.entries(checklist || {}).forEach(([playerId, state]) => {
      if (!state?.paid || byPlayerId.has(playerId)) return;
      const player = players.find((p) => p.id === playerId);
      byPlayerId.set(playerId, {
        playerId,
        playerName: player?.name || playerId,
        teamName: "No active team (paid)",
      });
    });

    const allPlayers = Array.from(byPlayerId.values());
    allPlayers.sort((a, b) => a.playerName.localeCompare(b.playerName));
    const filtered = checklistSearch ? allPlayers.filter(p => p.playerName.toLowerCase().includes(checklistSearch.toLowerCase()) || p.teamName.toLowerCase().includes(checklistSearch.toLowerCase())) : allPlayers;
    const notShowed = allPlayers.filter(p => !checklist[p.playerId]?.showed).length;
    const notPaid = allPlayers.filter(p => !checklist[p.playerId]?.paid).length;

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
          {canAdminRegisterTeam && (
            <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setRegOpen(true)}>Register Team</Button>
          )}
          <Button variant="outlined" onClick={() => setReplacementsDialogOpen(true)}>
            Manage Replacements
          </Button>
          {(tournament.registeredTeams?.length || 0) > 0 && (
            <Button variant="outlined" color="error" onClick={() => setDeleteAllTeamsOpen(true)}>
              Delete All Teams
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => setPendingInvitesDialogOpen(true)}
            sx={{ justifyContent: "space-between" }}
          >
            <span>⏳ Pending Invitations</span>
            <Chip
              label={overviewData?.pendingInvitations?.length || 0}
              size="small"
              color={(overviewData?.pendingInvitations?.length || 0) > 0 ? "warning" : "default"}
              sx={{ ml: 1, height: 18, fontSize: 10 }}
            />
          </Button>
          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            onClick={() => {
              setPartnerBoardDialogOpen(true);
              fetchPartnerBoardPlayers();
            }}
            sx={{ justifyContent: "space-between" }}
          >
            <span>Partner Board Players</span>
            <Chip
              label={partnerBoardPlayers.length}
              size="small"
              color={partnerBoardPlayers.length > 0 ? "info" : "default"}
              sx={{ ml: 1, height: 18, fontSize: 10 }}
            />
          </Button>
        </Box>

        <Grid container spacing={3}>
          {/* Registered teams list */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>Registered Teams ({tournament.registeredTeams?.length || 0})</Typography>
            <Stack spacing={1}>
              {(tournament.registeredTeams || []).length === 0
                ? <Typography color="text.secondary" variant="body2">No teams registered yet.</Typography>
                : (tournament.registeredTeams || []).map((team, i) => (
                  <Paper key={team.id} variant="outlined" sx={{ p: 1.5, ...(team.isBye ? { bgcolor: "action.hover" } : {}) }}>
                    {team.isBye ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ minWidth: 24 }}>#{i + 1}</Typography>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>{team.teamName || "BYE"}</Typography>
                          <Typography variant="caption" color="text.secondary">Placeholder — opponents auto-win</Typography>
                        </Box>
                        <Chip size="small" label="BYE" color="default" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                        <Tooltip title="Delete Bye team">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteRegisteredTeam(team)}
                              disabled={deletingRegisteredTeamId === team.id}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    ) : (() => {
                      const p1Showed = !!(team.player1Id && checklist[team.player1Id]?.showed);
                      const p2Showed = !!(team.player2Id && checklist[team.player2Id]?.showed);
                      const showedCount = (p1Showed ? 1 : 0) + (p2Showed ? 1 : 0);
                      const attendanceColor = showedCount === 2 ? "success" : showedCount === 1 ? "warning" : "error";

                      return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="body2" fontWeight={700} sx={{ minWidth: 24 }}>#{i + 1}</Typography>
                      <Box sx={{ flex: "1 1 140px", minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600}>{team.teamName || `${team.player1Name} & ${team.player2Name}`}</Typography>
                        {team.teamName && <Typography variant="caption" color="text.secondary">{team.player1Name} & {team.player2Name}</Typography>}
                        {(checklist[team.player1Id || ""]?.discountValue || checklist[team.player2Id || ""]?.discountValue) && (
                          <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                            {checklist[team.player1Id || ""]?.discountValue != null && (
                              <Chip
                                size="small"
                                color="secondary"
                                label={`🎟 ${team.player1Name}: ${checklist[team.player1Id || ""]?.discountType === "percentage" ? `${checklist[team.player1Id || ""]?.discountValue}%` : `€${checklist[team.player1Id || ""]?.discountValue}`} off`}
                                sx={{ height: 18, fontSize: 10 }}
                              />
                            )}
                            {checklist[team.player2Id || ""]?.discountValue != null && (
                              <Chip
                                size="small"
                                color="secondary"
                                label={`🎟 ${team.player2Name}: ${checklist[team.player2Id || ""]?.discountType === "percentage" ? `${checklist[team.player2Id || ""]?.discountValue}%` : `€${checklist[team.player2Id || ""]?.discountValue}`} off`}
                                sx={{ height: 18, fontSize: 10 }}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title={`${team.player1Name || "Player 1"}: ${p1Showed ? "Showed" : "Missing"}`}>
                          {p1Showed
                            ? <CheckCircleIcon fontSize="small" color="success" />
                            : <HighlightOffIcon fontSize="small" color="error" />}
                        </Tooltip>
                        <Tooltip title={`${team.player2Name || "Player 2"}: ${p2Showed ? "Showed" : "Missing"}`}>
                          {p2Showed
                            ? <CheckCircleIcon fontSize="small" color="success" />
                            : <HighlightOffIcon fontSize="small" color="error" />}
                        </Tooltip>
                        <Chip
                          size="small"
                          label={`${showedCount}/2`}
                          color={attendanceColor as any}
                          variant={showedCount === 2 ? "filled" : "outlined"}
                          sx={{ height: 20, fontSize: 11, ml: 0.5 }}
                        />
                      </Stack>
                      <Tooltip title="Delete team">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteRegisteredTeam(team)}
                            disabled={deletingRegisteredTeamId === team.id}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit team name">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditTeamName(team, 'registered team')}
                            disabled={renamingTeamId === team.id}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move to waitlist">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleMoveTeamToWaitlist(team, 'registered team')}
                            disabled={movingTeamToWaitlistId === team.id}
                            sx={{ ml: 0.5 }}
                          >
                            Waitlist
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                      );
                    })()}
                  </Paper>
                ))
              }
            </Stack>
          </Grid>

          {/* Invitation / registration status overview */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Registrations Overview
            </Typography>
            {overviewLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Stack spacing={1.5}>
                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    ✅ Confirmed Teams ({overviewData?.confirmedTeams?.length || 0})
                  </Typography>
                  {!overviewData?.confirmedTeams?.length ? (
                    <Typography variant="body2" color="text.secondary">No confirmed teams yet.</Typography>
                  ) : (
                    <Stack spacing={0.75}>
                      {overviewData.confirmedTeams.map((team: any, i: number) => (
                        <Box key={team.id || i} sx={{ p: 1, border: 1, borderColor: team.adminAdded ? "warning.light" : "divider", borderRadius: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                            <Typography variant="caption" fontWeight={700}>#{i + 1}</Typography>
                            <Typography variant="caption" fontWeight={700}>{team.teamName}</Typography>
                            {team.adminAdded && (
                              <Chip label="Added by Admin" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                            )}
                            <Tooltip title="Edit team name">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleEditTeamName(team, 'confirmed team')}
                                  disabled={renamingTeamId === team.id}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Move to waitlist">
                              <span style={{ marginLeft: 'auto' }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleMoveTeamToWaitlist(team, 'confirmed team')}
                                  disabled={movingTeamToWaitlistId === team.id}
                                  sx={{ height: 22, minWidth: 74, fontSize: 11, mr: 0.5 }}
                                >
                                  Waitlist
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete overview entry">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteOverviewEntry(team, 'confirmed team')}
                                  disabled={deletingOverviewEntryId === team.id}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {team.player1?.name} & {team.player2?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Registered: {formatOverviewDateTime(team.registeredAt) || "Unknown"}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    🕓 Waitlist Teams ({overviewData?.waitlistTeams?.length || 0})
                  </Typography>
                  {!overviewData?.waitlistTeams?.length ? (
                    <Typography variant="body2" color="text.secondary">No teams on the waitlist.</Typography>
                  ) : (
                    <Stack spacing={0.75}>
                      {overviewData.waitlistTeams.map((team: any, i: number) => (
                        <Box key={team.id || i} sx={{ p: 1, border: 1, borderColor: "info.light", borderRadius: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                            <Typography variant="caption" fontWeight={700}>#{i + 1}</Typography>
                            <Typography variant="caption" fontWeight={700}>{team.teamName}</Typography>
                            <Chip label="Waitlist" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                            {team.waitlistEmailSentAt && (
                              <Chip
                                label={`Email sent ${formatOverviewDateTime(team.waitlistEmailSentAt) || ''}`}
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ height: 18, fontSize: 10 }}
                              />
                            )}
                            <Tooltip title="Delete overview entry">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteOverviewEntry(team, 'waitlist team')}
                                  disabled={deletingOverviewEntryId === team.id}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ height: 24, fontSize: 11, px: 1 }}
                                disabled={waitlistEmailLoading}
                                onClick={() => handleOpenWaitlistEmailDialog(team)}
                              >
                                {waitlistEmailLoading && waitlistEmailTarget?.id === team.id ? 'Loading…' : 'Send Waitlist Email'}
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                sx={{ height: 24, fontSize: 11, px: 1 }}
                                disabled={promoteDialogLoading}
                                onClick={() => handleOpenPromoteDialog(team)}
                              >
                                {promoteDialogLoading && promoteDialogTarget?.id === team.id ? 'Loading…' : 'Promote to Registered'}
                              </Button>
                            </Box>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {team.player1?.name} & {team.player2?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Registered: {formatOverviewDateTime(team.registeredAt) || "Unknown"}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.25 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    ↩ Withdrawn Teams ({overviewData?.withdrawnTeams?.length || 0})
                  </Typography>
                  {!overviewData?.withdrawnTeams?.length ? (
                    <Typography variant="body2" color="text.secondary">No withdrawn teams.</Typography>
                  ) : (
                    <Stack spacing={0.75}>
                      {overviewData.withdrawnTeams.map((team: any, i: number) => (
                        <Box key={team.id || i} sx={{ p: 1, border: 1, borderColor: "warning.light", borderRadius: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                            <Typography variant="caption" fontWeight={700}>{team.teamName}</Typography>
                            <Chip label="Withdrawn" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                            <Tooltip title="Delete overview entry">
                              <span style={{ marginLeft: 'auto' }}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteOverviewEntry(team, 'withdrawn team')}
                                  disabled={deletingOverviewEntryId === team.id}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {team.player1?.name} & {team.player2?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Withdrawn by: {team.withdrewBy?.name || "Unknown"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Withdrawn at: {formatOverviewDateTime(team.withdrawnAt) || "Unknown"}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Paper>
              </Stack>
            )}
          </Grid>

          {/* Checklist */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>Player Checklist</Typography>
            {allPlayers.length > 0 && (
              <Alert severity={notShowed === 0 && notPaid === 0 ? "success" : "info"} sx={{ mb: 1.5 }}>
                {notShowed === 0 && notPaid === 0 ? "All present & paid ✓" : [notShowed > 0 && `${notShowed} not showed`, notPaid > 0 && `${notPaid} not paid`].filter(Boolean).join(" · ")}
              </Alert>
            )}
            <TextField size="small" fullWidth placeholder="Search player…" value={checklistSearch} onChange={e => setChecklistSearch(e.target.value)} sx={{ mb: 1 }} InputProps={{ startAdornment: <span style={{ marginRight: 6, color: "#aaa" }}>🔍</span> }} />
            {allPlayers.length > 0 && (
              <Box sx={{ display: "flex", px: 1, pb: 0.5, borderBottom: 1, borderColor: "divider" }}>
                <Typography variant="caption" fontWeight={700} sx={{ flex: 1 }}>Player</Typography>
                <Typography variant="caption" fontWeight={700} sx={{ width: 72, textAlign: "center" }}>Showed</Typography>
                <Typography variant="caption" fontWeight={700} sx={{ width: 72, textAlign: "center" }}>Paid</Typography>
              </Box>
            )}
            <Stack spacing={0.5}>
              {filtered.map(p => {
                const st = checklist[p.playerId] || { showed: false, paid: false };
                return (
                  <Box key={p.playerId} sx={{ display: "flex", alignItems: "center", px: 1, py: 0.5, borderRadius: 1, bgcolor: st.showed && st.paid ? "success.50" : "transparent", border: 1, borderColor: st.showed && st.paid ? "success.light" : "divider" }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600}>{p.playerName}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.teamName}</Typography>
                    </Box>
                    <Box sx={{ width: 72, display: "flex", justifyContent: "center" }}>
                      <Checkbox size="small" color="success" checked={st.showed} onChange={e => handleChecklistToggle(p.playerId, "showed", e.target.checked)} />
                    </Box>
                    <Box sx={{ width: 72, display: "flex", justifyContent: "center" }}>
                      <Checkbox size="small" color="primary" checked={st.paid} onChange={e => handleChecklistToggle(p.playerId, "paid", e.target.checked)} />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Grid>

        </Grid>

        {/* Delete all teams dialog */}
        <Dialog
          open={deleteAllTeamsOpen}
          onClose={() => !deletingAllTeams && setDeleteAllTeamsOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete all teams?</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2">
                This removes all {tournament.registeredTeams?.length || 0} registered teams
                (including test and Bye teams). Any generated groups and knockout schedule will be
                reset. This cannot be undone.
              </Typography>
              <TextField
                size="small"
                autoFocus
                label='Type "confirm" to continue'
                value={deleteAllTeamsConfirmText}
                onChange={e => setDeleteAllTeamsConfirmText(e.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setDeleteAllTeamsOpen(false); setDeleteAllTeamsConfirmText(""); }} disabled={deletingAllTeams}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={deleteAllTeamsConfirmText.trim().toLowerCase() !== "confirm" || deletingAllTeams}
              onClick={handleDeleteAllRegisteredTeams}
            >
              {deletingAllTeams ? "Deleting…" : "Delete All Teams"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Register team dialog */}
        <Dialog open={registerOpen} onClose={() => setRegOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Register Team</DialogTitle>
          <DialogContent>
            {(() => {
              const registeredPlayerIds = new Set(
                (tournament?.registeredTeams || []).flatMap(t => [t.player1Id, t.player2Id]).filter(Boolean)
              );
              const remainingSpots = Math.max(0, (tournament?.maxTeams || 0) - (tournament?.registeredTeams?.length || 0));
              return (
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={regBulkSaving || remainingSpots <= 0}
                    onClick={handleFillTournamentWithTestTeams}
                  >
                    {regBulkSaving ? "Filling…" : "Fill Tournament Capacity with Test Teams"}
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {remainingSpots <= 0
                      ? "Tournament is full."
                      : `Adds ${remainingSpots} temporary test team${remainingSpots === 1 ? "" : "s"} (test_1, test_2, …). They only exist inside this tournament and are not saved as players.`}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={regSaving || regBulkSaving || remainingSpots <= 0}
                    onClick={handleAddByeTeam}
                  >
                    {regSaving ? "Adding…" : "Add Bye Team (no players — opponents auto-win)"}
                  </Button>
                  <Autocomplete
                    options={players}
                    getOptionLabel={p => p.name}
                    getOptionDisabled={p => registeredPlayerIds.has(p.id)}
                    value={regPlayer1}
                    onChange={(_, v) => setRegPlayer1(v)}
                    renderInput={p => <TextField {...p} label="Player 1 *" size="small" />}
                  />
                  <Autocomplete
                    options={players.filter(p => p.id !== regPlayer1?.id)}
                    getOptionLabel={p => p.name}
                    getOptionDisabled={p => registeredPlayerIds.has(p.id)}
                    value={regPlayer2}
                    onChange={(_, v) => setRegPlayer2(v)}
                    renderInput={p => <TextField {...p} label="Player 2 *" size="small" />}
                  />
                  <TextField label="Team name (optional)" value={regTeamName} onChange={e => setRegTeamName(e.target.value)} size="small" />
                </Stack>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRegOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleRegisterTeam} disabled={regSaving || regBulkSaving || !regPlayer1 || !regPlayer2}>
              {regSaving ? "Registering…" : "Register"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={pendingInvitesDialogOpen}
          onClose={() => setPendingInvitesDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Pending Invitations ({overviewData?.pendingInvitations?.length || 0})
          </DialogTitle>
          <DialogContent dividers>
            {!overviewData?.pendingInvitations?.length ? (
              <Typography variant="body2" color="text.secondary">No pending invitations.</Typography>
            ) : (
              <Stack spacing={0.75}>
                {overviewData.pendingInvitations.map((inv: any) => (
                  <Box key={inv.id} sx={{ p: 1.25, border: 1, borderColor: inv.expired ? "error.light" : "warning.light", borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight={700}>
                      {inv.sender?.name} invited {inv.recipient?.name}
                    </Typography>
                    {inv.teamName && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Team: {inv.teamName}
                      </Typography>
                    )}
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.75 }}>
                      {inv.recipient?.isNewPlayer
                        ? <Chip label="New Player" size="small" color="warning" sx={{ height: 18, fontSize: 10 }} />
                        : inv.recipient?.isTournamentAccount
                          ? <Chip label="Tournament Account" size="small" color="info" sx={{ height: 18, fontSize: 10 }} />
                          : <Chip label="Existing Player" size="small" color="success" sx={{ height: 18, fontSize: 10 }} />}
                      <Chip
                        label={inv.expired ? "Expired" : `${inv.hoursLeft}h ${inv.minutesLeft}m left`}
                        size="small"
                        color={inv.expired ? "error" : inv.hoursLeft < 4 ? "warning" : "default"}
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    </Box>
                    {inv.personalMessage && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }} display="block">
                        "{inv.personalMessage}"
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPendingInvitesDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={partnerBoardDialogOpen}
          onClose={() => setPartnerBoardDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Partner Board Players ({partnerBoardPlayers.length})
          </DialogTitle>
          <DialogContent dividers>
            {partnerBoardLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : !partnerBoardPlayers.length ? (
              <Typography variant="body2" color="text.secondary">No players currently on the partner board for this tournament.</Typography>
            ) : (
              <>
                {(() => {
                  const activePlayers = partnerBoardPlayers.filter((p: any) => p.isActive);
                  const removedPlayers = partnerBoardPlayers.filter((p: any) => !p.isActive);

                  const renderPlayerCard = (player: any, isRemovedSection = false) => (
                    <Box
                      key={player.id}
                      sx={{
                        p: 1.25,
                        border: 1,
                        borderColor: isRemovedSection ? "error.light" : "divider",
                        backgroundColor: isRemovedSection ? "error.50" : "transparent",
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                        <Typography variant="body2" fontWeight={700}>{player.playerName}</Typography>
                        {player.isActive ? (
                          <Chip
                            label="ACTIVE"
                            size="small"
                            color="success"
                            sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                          />
                        ) : player.removedBy === "player" ? (
                          <Chip
                            label="REMOVED BY PLAYER"
                            size="small"
                            color="error"
                            sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                          />
                        ) : player.removedBy === "admin" ? (
                          <Chip
                            label="REMOVED BY ADMIN"
                            size="small"
                            color="error"
                            sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                          />
                        ) : player.removedBy === "system" ? (
                          <Chip
                            label="REMOVED BY SYSTEM"
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                          />
                        ) : (
                          <Chip
                            label="REMOVED"
                            size="small"
                            color="error"
                            sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
                          />
                        )}
                        {player.level != null && (
                          <Chip label={`Level ${player.level}`} size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                        )}
                        {player.isLocked && (
                          <Chip label="Locked" size="small" color="warning" sx={{ height: 18, fontSize: 10 }} />
                        )}
                        {(player.preferredLevelMin || player.preferredLevelMax) && (
                          <Chip
                            label={`Pref Lvl ${player.preferredLevelMin || "?"}-${player.preferredLevelMax || "?"}`}
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ height: 18, fontSize: 10 }}
                          />
                        )}
                        {player.isActive && (
                          <Button
                            size="small"
                            color="error"
                            variant="contained"
                            sx={{ ml: "auto" }}
                            disabled={removingPartnerBoardEntryId === player.id}
                            onClick={() => handleAdminRemovePartnerBoardEntry(player)}
                          >
                            {removingPartnerBoardEntryId === player.id ? "Removing..." : "Remove Entry"}
                          </Button>
                        )}
                      </Box>
                      {!player.isActive && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {player.removedReason === "team-registered"
                            ? "Auto-removed because the player got a team registration."
                            : player.removedReason === "self-removed"
                              ? "Player removed their own partner board entry."
                              : player.removedReason === "admin-removed"
                                ? "Removed by admin for moderation."
                              : "Entry is inactive."}
                        </Typography>
                      )}
                      {player.bio && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75, fontStyle: "italic" }}>
                          "{player.bio}"
                        </Typography>
                      )}
                    </Box>
                  );

                  return (
                    <Stack spacing={1.25}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                          Active on Partner Board ({activePlayers.length})
                        </Typography>
                        {!activePlayers.length ? (
                          <Typography variant="caption" color="text.secondary">No active players on the board.</Typography>
                        ) : (
                          <Stack spacing={0.75}>
                            {activePlayers.map((player: any) => renderPlayerCard(player, false))}
                          </Stack>
                        )}
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" color="error.main" sx={{ mb: 0.5, fontWeight: 700 }}>
                          Removed from Partner Board ({removedPlayers.length})
                        </Typography>
                        {!removedPlayers.length ? (
                          <Typography variant="caption" color="text.secondary">No removed entries.</Typography>
                        ) : (
                          <Stack spacing={0.75}>
                            {removedPlayers.map((player: any) => renderPlayerCard(player, true))}
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  );
                })()}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPartnerBoardDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <TournamentReplacementDialog
          open={replacementsDialogOpen}
          onClose={() => setReplacementsDialogOpen(false)}
          tournament={tournament}
          players={players}
          onApplied={fetchTournament}
          onNotify={msg}
        />

        <Dialog
          open={waitlistEmailDialogOpen}
          onClose={() => setWaitlistEmailDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Waitlist Email Preview{waitlistEmailTarget?.teamName ? ` - ${waitlistEmailTarget.teamName}` : ''}
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {"Recipients: "}
              {waitlistEmailRecipients.length > 0
                ? waitlistEmailRecipients.map((r: any) => `${r.name}${r.email ? ` (${r.email})` : ' (no email)'}`).join(', ')
                : 'No recipients found'}
            </Typography>

            <Stack spacing={1.5}>
              <TextField
                label="Subject"
                value={waitlistEmailSubject}
                onChange={(e) => setWaitlistEmailSubject(e.target.value)}
                fullWidth
              />
              <TextField
                label="Message"
                value={waitlistEmailMessage}
                onChange={(e) => setWaitlistEmailMessage(e.target.value)}
                multiline
                rows={10}
                fullWidth
              />

              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Live Preview</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                  {waitlistEmailSubject || '(No subject)'}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {waitlistEmailMessage || '(No message)'}
                </Typography>
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setWaitlistEmailDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSendWaitlistEmail}
              disabled={waitlistEmailSending || !waitlistEmailSubject.trim() || !waitlistEmailMessage.trim()}
            >
              {waitlistEmailSending ? 'Sending…' : 'Send Email'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={promoteDialogOpen}
          onClose={() => !promoteDialogSending && setPromoteDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Promote to Registered{promoteDialogTarget?.teamName ? ` — ${promoteDialogTarget.teamName}` : ''}
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              This will move the team from the waitlist to registered teams and send them the email below.
              They will receive a separate payment email shortly to confirm their spot.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {"Recipients: "}
              {promoteDialogRecipients.length > 0
                ? promoteDialogRecipients.map((r: any) => `${r.name}${r.email ? ` (${r.email})` : ' (no email)'}`).join(', ')
                : 'No recipients found'}
            </Typography>

            <Stack spacing={1.5}>
              <TextField
                label="Subject"
                value={promoteDialogSubject}
                onChange={(e) => setPromoteDialogSubject(e.target.value)}
                fullWidth
                disabled={promoteDialogSending}
              />
              <TextField
                label="Message"
                value={promoteDialogMessage}
                onChange={(e) => setPromoteDialogMessage(e.target.value)}
                multiline
                rows={10}
                fullWidth
                disabled={promoteDialogSending}
              />

              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Live Preview</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                  {promoteDialogSubject || '(No subject)'}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {promoteDialogMessage || '(No message)'}
                </Typography>
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPromoteDialogOpen(false)} disabled={promoteDialogSending}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleConfirmPromote}
              disabled={promoteDialogSending || !promoteDialogSubject.trim() || !promoteDialogMessage.trim()}
            >
              {promoteDialogSending ? 'Promoting…' : 'Promote & Send Email'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  const renderGiftsTab = () => {
    if (!tournament) return null;
    const tournamentPlayers = getTournamentPlayers();

    return (
      <Box sx={{ p: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
          <FormControlLabel
            control={<Switch checked={giftsEnabled} onChange={e => handleGiftsEnabledToggle(e.target.checked)} />}
            label="Enable gift pickup for this tournament"
          />
          <Button
            variant="outlined"
            disabled={!giftsEnabled}
            onClick={() => navigate(`/admin/gift-scan?t=${tournament.id}`)}
          >
            Scan QR
          </Button>
        </Stack>

        <TextField
          label="Default gifts (one per line, optional)"
          placeholder={"Sponsor Gift A\nSponsor Gift B\nTournament Towel"}
          multiline
          minRows={3}
          fullWidth
          disabled={!giftsEnabled}
          value={defaultGiftsText}
          onChange={(e) => setDefaultGiftsText(e.target.value)}
          onBlur={handleDefaultGiftDescriptionsBlur}
          helperText="Admins can choose from these options when confirming QR pickup."
          sx={{ mb: 2 }}
        />

        {defaultGiftDescriptions.length > 0 && (
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
            {defaultGiftDescriptions.map((gift) => (
              <Chip key={gift} label={gift} size="small" variant="outlined" />
            ))}
          </Stack>
        )}

        {!giftsEnabled ? (
          <Alert severity="info">Gift pickup is currently disabled for this tournament.</Alert>
        ) : tournamentPlayers.length === 0 ? (
          <Typography color="text.secondary" variant="body2">No registered players yet.</Typography>
        ) : (
          <Stack spacing={0.5}>
            {tournamentPlayers.map((p) => {
              const redemption = giftRedemptions[p.playerId] || { redeemed: false };
              return (
                <Box
                  key={p.playerId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    flexWrap: "wrap",
                    bgcolor: redemption.redeemed ? "success.50" : "transparent",
                    border: 1,
                    borderColor: redemption.redeemed ? "success.light" : "divider",
                  }}
                >
                  <Box sx={{ minWidth: 160 }}>
                    <Typography variant="body2" fontWeight={600}>{p.playerName}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.teamName}</Typography>
                  </Box>
                  <TextField
                    size="small"
                    placeholder={defaultGiftDescriptions[0] || "Gift description"}
                    value={redemption.giftDescription ?? ""}
                    onChange={e => handleGiftOverrideChange(p.playerId, e.target.value)}
                    onBlur={() => handleGiftOverrideBlur(p.playerId)}
                    sx={{ flex: 1, minWidth: 160 }}
                  />
                  <FormControlLabel
                    sx={{ ml: "auto" }}
                    control={
                      <Checkbox
                        size="small"
                        color="success"
                        checked={!!redemption.redeemed}
                        onChange={e => handleGiftRedeemToggle(p.playerId, e.target.checked)}
                      />
                    }
                    label="Picked up"
                  />
                  {redemption.redeemed && redemption.redeemedByAdminName && (
                    <Typography variant="caption" color="text.secondary">
                      by {redemption.redeemedByAdminName}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    );
  };

  const renderGroupsTab = () => {
    if (!tournament) return null;
    const hasGroups = (tournament.groups?.length || 0) > 0;
    const reg = tournament.registeredTeams?.length || 0;

    return (
      <Box sx={{ p: 3 }}>
        {!hasGroups ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <GroupIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
            <Typography variant="h5" gutterBottom>Generate Groups</Typography>
            <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: "auto" }}>
              Group generation assigns registered teams into groups and creates all round-robin matches. You need at least {tournament.minTeams} teams.
            </Typography>
            <Alert severity={reg >= tournament.minTeams ? "success" : "warning"} sx={{ mb: 3, maxWidth: 400, mx: "auto" }}>
              {reg} / {tournament.minTeams} minimum teams registered
            </Alert>
            <Tooltip title={reg < tournament.minTeams ? `Need ${tournament.minTeams - reg} more teams` : ""}>
              <span>
                <Button variant="contained" size="large" startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <GroupIcon />} onClick={handleGenerateGroups} disabled={generating || reg < tournament.minTeams}>
                  {generating ? "Generating…" : "Generate Groups"}
                </Button>
              </span>
            </Tooltip>
          </Box>
        ) : (
          <Stack spacing={2}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Typography variant="h6">{tournament.groups.length} Groups · {reg} Teams</Typography>
              <Button
                variant="outlined"
                color="warning"
                onClick={handleResetGroups}
                disabled={resettingGroups || generatingBracket}
              >
                {resettingGroups ? "Resetting..." : "Reset Groups"}
              </Button>
            </Box>
            <Grid container spacing={2}>
              {tournament.groups.map(g => (
                <Grid item xs={12} sm={6} md={4} key={g.id}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>{g.name}</Typography>
                    <Stack spacing={0.5}>
                      {g.teamIds.map(tid => {
                        const t = tournament.registeredTeams?.find(r => r.id === tid);
                        return <Typography key={tid} variant="body2">{t?.teamName || `${t?.player1Name || "?"} & ${t?.player2Name || "?"}`}</Typography>;
                      })}
                    </Stack>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary">Standings</Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {(
                        g.standings && g.standings.length > 0
                          ? [...g.standings].sort((a, b) => b.points !== a.points ? b.points - a.points : b.gamesDiff !== a.gamesDiff ? b.gamesDiff - a.gamesDiff : b.gamesWon - a.gamesWon)
                          : g.teamIds.map(id => ({ teamId: id, wins: 0, losses: 0, points: 0, draws: 0, gamesWon: 0, gamesLost: 0, gamesDiff: 0, matchesPlayed: 0 }))
                      ).map((s, idx) => (
                        <Box key={s.teamId} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography variant="caption" sx={{ width: 18, color: "text.secondary" }}>{idx + 1}.</Typography>
                          <Typography variant="caption" sx={{ flex: 1 }} noWrap>{getTeamName(s.teamId)}</Typography>
                          <Chip label={`${s.wins}W`} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.6rem" }} />
                          <Chip label={`${s.losses}L`} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.6rem" }} />
                          <Chip label={`GW ${s.gamesWon}`} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.6rem" }} />
                          <Chip label={`GD ${s.gamesDiff > 0 ? "+" : ""}${s.gamesDiff}`} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.6rem" }} />
                          <Chip label={`${s.points}pts`} size="small" color="primary" sx={{ height: 18, fontSize: "0.6rem" }} />
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Stack>
        )}
      </Box>
    );
  };

  const renderPaymentTab = () => {
    if (!tournament) return null;
    const allTournamentPlayers = getTournamentPlayers();
    const unpaidPlayers = allTournamentPlayers.filter((p) => !p.paid);
    const pricingWindows: Array<{ key: PaymentWindowKey; label: string }> = [
      { key: "earlyBird", label: "Early Bird" },
      { key: "normalRegistration", label: "Normal Registration" },
      { key: "lateRegistration", label: "Late Registration" },
    ];

    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={paymentSettings.payInCash}
                      onChange={(e) => setPaymentSettings((prev) => ({ ...prev, payInCash: e.target.checked }))}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700}>Payment is made in cash at the venue</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Leave unchecked for online payment (bank / PayPal, with payment emails). When checked, no payment
                        email is sent and players are not told a payment email is coming.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", mb: 2 }}
                />
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  Bank & PayPal Details
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  These details are used in payment reminder emails.
                </Typography>

                <Stack spacing={1.5}>
                  <TextField
                    label="Account Name"
                    value={paymentSettings.accountName}
                    onChange={(e) => setPaymentSettings((prev) => ({ ...prev, accountName: e.target.value }))}
                    fullWidth
                  />
                  <TextField
                    label="Bank Name (optional)"
                    value={paymentSettings.bankName}
                    onChange={(e) => setPaymentSettings((prev) => ({ ...prev, bankName: e.target.value }))}
                    fullWidth
                  />
                  <TextField
                    label="IBAN"
                    value={paymentSettings.iban}
                    onChange={(e) => setPaymentSettings((prev) => ({ ...prev, iban: e.target.value }))}
                    inputProps={{ style: { fontFamily: "monospace" } }}
                    fullWidth
                  />
                  <TextField
                    label="BIC"
                    value={paymentSettings.bic}
                    onChange={(e) => setPaymentSettings((prev) => ({ ...prev, bic: e.target.value }))}
                    inputProps={{ style: { fontFamily: "monospace" } }}
                    fullWidth
                  />
                  <TextField
                    label="PayPal Payment Link (optional)"
                    value={paymentSettings.paypalLink}
                    onChange={(e) => setPaymentSettings((prev) => ({ ...prev, paypalLink: e.target.value }))}
                    fullWidth
                  />

                  {/* PayPal QR Code upload */}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>PayPal QR Code (optional)</Typography>
                    {paymentSettings.paypalQrCodeUrl ? (
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <Box
                          component="img"
                          src={paymentSettings.paypalQrCodeUrl}
                          alt="PayPal QR Code"
                          sx={{ width: 120, height: 120, objectFit: "contain", border: 1, borderColor: "divider", borderRadius: 1, p: 0.5, bgcolor: "#fff" }}
                        />
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                            QR will be embedded in the payment reminder email.
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={handleQrClear}
                          >
                            Remove QR
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                          Upload a PNG of your PayPal QR code. It will be shown in payment reminder emails.
                        </Typography>
                        <Button
                          component="label"
                          variant="outlined"
                          size="small"
                          disabled={qrUploading}
                        >
                          {qrUploading ? "Uploading…" : "Upload QR Code (PNG)"}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/*"
                            hidden
                            onChange={handleQrUpload}
                          />
                        </Button>
                      </Box>
                    )}
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Registration Pricing Windows</Typography>
                    <Stack spacing={1.5}>
                      {pricingWindows.map((windowDef) => (
                        <Box key={windowDef.key} sx={{ p: 1.25, border: 1, borderColor: "divider", borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                            {windowDef.label}
                          </Typography>
                          <Grid container spacing={1.25}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Start"
                                type="datetime-local"
                                value={paymentSettings[windowDef.key].startAt}
                                onChange={(e) => setPaymentSettings((prev) => ({
                                  ...prev,
                                  [windowDef.key]: {
                                    ...prev[windowDef.key],
                                    startAt: e.target.value,
                                  },
                                }))}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="End"
                                type="datetime-local"
                                value={paymentSettings[windowDef.key].endAt}
                                onChange={(e) => setPaymentSettings((prev) => ({
                                  ...prev,
                                  [windowDef.key]: {
                                    ...prev[windowDef.key],
                                    endAt: e.target.value,
                                  },
                                }))}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="Price (€)"
                                type="number"
                                value={paymentSettings[windowDef.key].price}
                                onChange={(e) => setPaymentSettings((prev) => ({
                                  ...prev,
                                  [windowDef.key]: {
                                    ...prev[windowDef.key],
                                    price: e.target.value,
                                  },
                                }))}
                                inputProps={{ min: 0, step: 0.01 }}
                                fullWidth
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Refund Policy</Typography>
                    <Stack spacing={1.25}>
                      <TextField
                        label="More than 5 days before the tournament"
                        value={paymentSettings.refundPolicy.moreThanFiveDays}
                        onChange={(e) => setPaymentSettings((prev) => ({
                          ...prev,
                          refundPolicy: {
                            ...prev.refundPolicy,
                            moreThanFiveDays: e.target.value,
                          },
                        }))}
                        fullWidth
                      />
                      <TextField
                        label="Between 5 days and 2 days before the tournament"
                        value={paymentSettings.refundPolicy.betweenFiveAndTwoDays}
                        onChange={(e) => setPaymentSettings((prev) => ({
                          ...prev,
                          refundPolicy: {
                            ...prev.refundPolicy,
                            betweenFiveAndTwoDays: e.target.value,
                          },
                        }))}
                        fullWidth
                      />
                      <TextField
                        label="Within 48 hours of the tournament"
                        value={paymentSettings.refundPolicy.within48Hours}
                        onChange={(e) => setPaymentSettings((prev) => ({
                          ...prev,
                          refundPolicy: {
                            ...prev.refundPolicy,
                            within48Hours: e.target.value,
                          },
                        }))}
                        fullWidth
                      />
                    </Stack>
                  </Box>
                </Stack>

                <Box sx={{ mt: 2 }}>
                  <Button variant="contained" onClick={handleSavePaymentSettings} disabled={savingPaymentSettings}>
                    {savingPaymentSettings ? "Saving..." : "Save Payment Settings"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  Payment Reminder Email
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {"You can edit this template before sending. Placeholders: {{playerName}}, {{tournamentName}}, {{teamName}}, {{accountName}}, {{bankName}}, {{iban}}, {{bic}}, {{paymentReference}}, {{registrationDateTime}}, {{registrationWindowName}}, {{registrationPrice}}, {{registrationWindowStart}}, {{registrationWindowEnd}}, {{refundMoreThanFiveDays}}, {{refundBetweenFiveAndTwoDays}}, {{refundWithin48Hours}}. The PayPal QR code is inserted automatically above the footer when uploaded."}
                </Typography>

                <Stack spacing={1.5}>
                  <TextField
                    label="Email Subject"
                    value={paymentSettings.emailSubject}
                    onChange={(e) => setPaymentSettings((prev) => ({ ...prev, emailSubject: e.target.value }))}
                    fullWidth
                  />
                  <TextField
                    label="Email Message"
                    value={paymentSettings.emailMessage}
                    onChange={(e) => setPaymentSettings((prev) => ({ ...prev, emailMessage: e.target.value }))}
                    multiline
                    rows={10}
                    fullWidth
                  />
                </Stack>

                <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Recipients</Typography>
                  <ToggleButtonGroup
                    value={paymentRecipientMode}
                    exclusive
                    onChange={(_, v) => v && setPaymentRecipientMode(v)}
                    size="small"
                    sx={{ mb: 1.5 }}
                  >
                    <ToggleButton value="all">All Unpaid ({unpaidPlayers.length})</ToggleButton>
                    <ToggleButton value="specific">Specific Players</ToggleButton>
                  </ToggleButtonGroup>

                  {paymentRecipientMode === "specific" && (
                    <Box sx={{ maxHeight: 220, overflowY: "auto", border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}>
                      {unpaidPlayers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No unpaid players found.</Typography>
                      ) : (
                        unpaidPlayers.map((p) => (
                          <FormControlLabel
                            key={p.playerId}
                            control={
                              <Checkbox
                                checked={selectedPaymentPlayerIds.includes(p.playerId)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedPaymentPlayerIds((prev) => [...prev, p.playerId]);
                                  } else {
                                    setSelectedPaymentPlayerIds((prev) => prev.filter((id) => id !== p.playerId));
                                  }
                                }}
                                size="small"
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight={600}>{p.playerName}</Typography>
                                <Typography variant="caption" color="text.secondary">{p.teamName}</Typography>
                              </Box>
                            }
                            sx={{ display: "flex", alignItems: "flex-start", m: 0 }}
                          />
                        ))
                      )}
                    </Box>
                  )}
                </Box>

                <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="caption" color="text.secondary">
                    Unpaid players: {unpaidPlayers.length}
                    {paymentSettings.payInCash && " · Payment emails are disabled (cash at the venue)"}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PaymentIcon />}
                    disabled={
                      sendingPaymentEmails ||
                      paymentSettings.payInCash ||
                      unpaidPlayers.length === 0 ||
                      (paymentRecipientMode === "specific" && selectedPaymentPlayerIds.length === 0)
                    }
                    onClick={handleSendPaymentEmails}
                  >
                    {sendingPaymentEmails ? "Sending..." : "Send Payment Email"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderEmailTab = () => {
    if (!tournament) return null;

    const recipientCounts: Array<{ mode: EmailRecipientMode; label: string }> = [
      { mode: "all", label: `All Players (${emailRoster.length})` },
      { mode: "confirmed", label: `Confirmed (${emailRoster.filter((r) => r.confirmed).length})` },
      { mode: "waitlist", label: `Waitlist (${emailRoster.filter((r) => r.waitlist).length})` },
      { mode: "paid", label: `Paid (${emailRoster.filter((r) => r.confirmed && r.paid).length})` },
      { mode: "unpaid", label: `Unpaid (${emailRoster.filter((r) => r.confirmed && !r.paid).length})` },
      { mode: "specific", label: "Specific Players" },
      { mode: "outside", label: `Outside Tournament (${outsideRoster.length})` },
    ];

    const noEmailCount = emailTargets.filter((t) => !t.hasEmail).length;

    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                  Compose Email
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {"Available variables: {{playerName}}, {{partnerName}}, {{teamName}}, {{tournamentName}}. They will be replaced with each recipient's details when the email is sent."}
                </Typography>
                <Stack spacing={1.5}>
                  <TextField
                    label="Email Subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Email Message"
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    multiline
                    rows={10}
                    fullWidth
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="h6" fontWeight={700}>Recipients</Typography>
                  {emailRosterLoading && <CircularProgress size={18} />}
                </Box>

                <ToggleButtonGroup
                  value={emailRecipientMode}
                  exclusive
                  onChange={(_, v) => {
                    if (!v) return;
                    // Clear selection when switching between in-tournament and outside modes
                    if (v === "outside" || emailRecipientMode === "outside") {
                      setSelectedEmailPlayerIds([]);
                      setOutsideSearch("");
                    }
                    setEmailRecipientMode(v);
                  }}
                  size="small"
                  sx={{ mb: 1.5, flexWrap: "wrap" }}
                >
                  {recipientCounts.map((rc) => (
                    <ToggleButton key={rc.mode} value={rc.mode}>{rc.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>

                {emailRecipientMode === "specific" && (
                  <Box sx={{ maxHeight: 260, overflowY: "auto", border: 1, borderColor: "divider", borderRadius: 1, p: 1, mb: 1.5 }}>
                    {emailRoster.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No players found.</Typography>
                    ) : (
                      emailRoster.map((p) => (
                        <FormControlLabel
                          key={p.playerId}
                          control={
                            <Checkbox
                              checked={selectedEmailPlayerIds.includes(p.playerId)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedEmailPlayerIds((prev) => [...prev, p.playerId]);
                                } else {
                                  setSelectedEmailPlayerIds((prev) => prev.filter((id) => id !== p.playerId));
                                }
                              }}
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {p.playerName} {!p.hasEmail && <Chip label="No email" size="small" color="warning" sx={{ ml: 0.5, height: 16, fontSize: "0.6rem" }} />}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {p.teamName} · {p.waitlist ? "Waitlist" : "Confirmed"} · {p.paid ? "Paid" : "Unpaid"}
                              </Typography>
                            </Box>
                          }
                          sx={{ display: "flex", alignItems: "flex-start", m: 0 }}
                        />
                      ))
                    )}
                  </Box>
                )}

                {emailRecipientMode === "outside" && (
                  <Box sx={{ mb: 1.5 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search by name…"
                      value={outsideSearch}
                      onChange={(e) => setOutsideSearch(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {selectedEmailPlayerIds.length} selected
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button size="small" variant="text" sx={{ fontSize: 11, p: 0, minWidth: 0 }}
                          onClick={() => setSelectedEmailPlayerIds(outsideRoster.map((p) => p.playerId))}>
                          Select all
                        </Button>
                        <Button size="small" variant="text" sx={{ fontSize: 11, p: 0, minWidth: 0 }}
                          onClick={() => setSelectedEmailPlayerIds([])}>
                          Clear
                        </Button>
                      </Box>
                    </Box>
                    <Box sx={{ maxHeight: 260, overflowY: "auto", border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}>
                      {outsideRoster.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">All players in the system are already in this tournament.</Typography>
                      ) : (
                        outsideRoster
                          .filter((p) => !outsideSearch || p.playerName.toLowerCase().includes(outsideSearch.toLowerCase()))
                          .map((p) => (
                            <FormControlLabel
                              key={p.playerId}
                              control={
                                <Checkbox
                                  checked={selectedEmailPlayerIds.includes(p.playerId)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedEmailPlayerIds((prev) => [...prev, p.playerId]);
                                    } else {
                                      setSelectedEmailPlayerIds((prev) => prev.filter((id) => id !== p.playerId));
                                    }
                                  }}
                                  size="small"
                                />
                              }
                              label={
                                <Typography variant="body2">
                                  {p.playerName} {!p.hasEmail && <Chip label="No email" size="small" color="warning" sx={{ ml: 0.5, height: 16, fontSize: "0.6rem" }} />}
                                </Typography>
                              }
                              sx={{ display: "flex", alignItems: "center", m: 0 }}
                            />
                          ))
                      )}
                    </Box>
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  {emailTargets.length} recipient(s) selected{noEmailCount > 0 ? `, ${noEmailCount} without an email on file (will be skipped)` : ""}
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<PreviewIcon />}
                    disabled={emailTargets.length === 0 || !emailSubject.trim() || !emailMessage.trim()}
                    onClick={() => setEmailPreviewOpen(true)}
                  >
                    Preview
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Preview Dialog */}
        <Dialog open={emailPreviewOpen} onClose={() => setEmailPreviewOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Email Preview</DialogTitle>
          <DialogContent>
            {(() => {
              const preview = buildEmailPreview();
              return (
                <>
                  <Typography variant="caption" color="text.secondary">
                    Showing preview for {preview.sampleName || "a sample recipient"}. This will be sent to {emailTargets.length} recipient(s).
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1.5 }}>
                    <Typography variant="subtitle2" color="text.secondary">Subject</Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>{preview.subject}</Typography>
                    <Typography variant="subtitle2" color="text.secondary">Message</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{preview.message}</Typography>
                  </Paper>
                </>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailPreviewOpen(false)} disabled={sendingCustomEmail}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSendCustomEmail}
              disabled={sendingCustomEmail || emailTargets.length === 0}
            >
              {sendingCustomEmail ? "Sending..." : `Send to ${emailTargets.length} Player(s)`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Result Dialog */}
        <Dialog open={!!emailSendResult} onClose={() => setEmailSendResult(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Email Send Results</DialogTitle>
          <DialogContent>
            {emailSendResult && (
              <>
                <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
                  <Chip label={`Total: ${emailSendResult.total}`} />
                  <Chip label={`Sent: ${emailSendResult.sent}`} color="success" />
                  <Chip label={`Failed: ${emailSendResult.failed}`} color="error" />
                  <Chip label={`Skipped: ${emailSendResult.skipped}`} color="warning" />
                </Stack>
                {(emailSendResult.failed > 0 || emailSendResult.skipped > 0) && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Failed / Skipped</Typography>
                    <List dense sx={{ maxHeight: 260, overflowY: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
                      {emailSendResult.results
                        .filter((r) => r.status !== "sent")
                        .map((r) => (
                          <ListItem key={r.playerId}>
                            <ListItemText
                              primary={r.playerName}
                              secondary={`${r.status === "failed" ? "Failed" : "Skipped"}${r.reason ? ` — ${r.reason}` : ""}`}
                            />
                          </ListItem>
                        ))}
                    </List>
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailSendResult(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  const renderScheduleTab = () => {
    if (!tournament) return null;
    const hasSchedule = (tournament.groups || []).some(g => (g.matchResults || []).some(m => m.startTime))
      || (tournament.knockoutBracket?.mastersCup || []).some(m => m.startTime)
      || (tournament.knockoutBracket?.tourCup || []).some(m => m.startTime);
    const totalMatches = (tournament.groups || []).reduce((s, g) => s + (g.matchResults?.length || 0), 0)
      + (tournament.knockoutBracket?.mastersCup?.length || 0)
      + (tournament.knockoutBracket?.tourCup?.length || 0);

    const handlePrintSchedule = () => {
      const bracketNames = getBracketNamesForOrg();
      const teams = tournament.registeredTeams || [];
      const getTeamLabel = (id?: string) => {
        const t = teams.find(team => team.id === id);
        if (!t) return "?";
        return t.teamName || `${t.player1Name || "?"} & ${t.player2Name || "?"}`;
      };

      const printableMatches: {
        slot: string;
        timestamp: number;
        court: number;
        title: string;
        subtitle: string;
      }[] = [];

      (tournament.groups || []).forEach(group => {
        (group.matchResults || []).forEach(match => {
          if ((match as any).isCancelled) return;
          if (!match.startTime || !match.courtNumber) return;
          const ts = new Date(match.startTime).getTime();
          printableMatches.push({
            slot: new Date(match.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            timestamp: ts,
            court: match.courtNumber,
            title: `${getTeamLabel(match.team1Id)} vs ${getTeamLabel(match.team2Id)}`,
            subtitle: group.name,
          });
        });
      });

      const ROUND_LABELS: Record<string, string> = {
        "round-of-32": "Round of 32",
        "round-of-16": "Round of 16",
        "quarter-finals": "Quarter-Finals",
        "semi-finals": "Semi-Finals",
        "final": "Final",
        "consolation-semi-finals": "Consolation Semi-Finals",
        "third-place": "Third Place",
        "fifth-place": "Fifth Place",
        "seventh-place": "Seventh Place",
      };

      const pushKnockoutMatches = (matches: any[], bracketLabel: string) => {
        matches.forEach(match => {
          if (match.isCancelled) return;
          if (!match.startTime || !match.courtNumber) return;
          const ts = new Date(match.startTime).getTime();
          const title = match.team1Id && match.team2Id
            ? `${getTeamLabel(match.team1Id)} vs ${getTeamLabel(match.team2Id)}`
            : (match.team1Placeholder && match.team2Placeholder
              ? `${match.team1Placeholder} vs ${match.team2Placeholder}`
              : `Match #${match.matchNumber || "?"}`);

          printableMatches.push({
            slot: new Date(match.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            timestamp: ts,
            court: match.courtNumber,
            title,
            subtitle: `${bracketLabel} · ${ROUND_LABELS[match.round] || match.round || "Knockout"}`,
          });
        });
      };

      pushKnockoutMatches(tournament.knockoutBracket?.mastersCup || [], bracketNames.primary);
      pushKnockoutMatches(tournament.knockoutBracket?.tourCup || [], bracketNames.secondary);

      if (printableMatches.length === 0) {
        msg("No scheduled matches to print yet.");
        return;
      }

      const courts = Array.from(new Set(printableMatches.map(m => m.court))).sort((a, b) => a - b);
      const slotOrder = Array.from(new Set(printableMatches.map(m => `${m.timestamp}__${m.slot}`)))
        .map(key => {
          const split = key.split("__");
          return { timestamp: Number(split[0]), slot: split[1] || "" };
        })
        .sort((a, b) => a.timestamp - b.timestamp);

      const cellMap = new Map<string, typeof printableMatches>();
      printableMatches.forEach(match => {
        const key = `${match.timestamp}-${match.court}`;
        const existing = cellMap.get(key) || [];
        existing.push(match);
        cellMap.set(key, existing);
      });

      let html = `<html><head><title>${tournament.name} - Schedule</title>`;
      html += `<style>
        body { font-family: Arial, sans-serif; margin: 20px; color: #222; }
        h1 { margin: 0 0 8px 0; }
        p.meta { margin: 0 0 14px 0; color: #555; font-size: 12px; }
        table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        th, td { border: 1px solid #cfcfcf; vertical-align: top; padding: 8px; }
        th { background: #f5f7fa; font-weight: 700; text-align: center; }
        th.slot-col { width: 92px; }
        td.slot-cell { font-weight: 700; text-align: center; background: #fafafa; }
        .match { margin-bottom: 6px; padding: 6px; border-radius: 4px; background: #f8fbff; border: 1px solid #e2ecf7; }
        .match:last-child { margin-bottom: 0; }
        .title { font-size: 12px; font-weight: 700; line-height: 1.25; }
        .subtitle { font-size: 11px; color: #556; margin-top: 2px; }
        .empty { color: #999; font-size: 11px; text-align: center; padding-top: 10px; }
        @media print {
          body { margin: 0; }
          thead { display: table-header-group; }
          tr, td, th { page-break-inside: avoid; }
        }
      </style></head><body>`;

      html += `<h1>${tournament.name} - Schedule</h1>`;
      html += `<p class="meta">Generated: ${new Date().toLocaleString()}</p>`;
      html += `<table><thead><tr><th class="slot-col">Time</th>${courts.map(c => `<th>Court ${c}</th>`).join("")}</tr></thead><tbody>`;

      slotOrder.forEach(slot => {
        html += `<tr><td class="slot-cell">${slot.slot}</td>`;
        courts.forEach(court => {
          const matches = cellMap.get(`${slot.timestamp}-${court}`) || [];
          if (matches.length === 0) {
            html += `<td><div class="empty">-</div></td>`;
            return;
          }
          html += `<td>${matches.map(m => `<div class="match"><div class="title">${m.title}</div><div class="subtitle">${m.subtitle}</div></div>`).join("")}</td>`;
        });
        html += `</tr>`;
      });

      html += `</tbody></table></body></html>`;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
        setTimeout(() => printWindow.print(), 250);
      }
    };

    return (
      <Box sx={{ p: 0 }}>
        {totalMatches > 0 && (
          <Box sx={{ px: 3, pt: 2, pb: 0, display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ChecklistIcon />}
              onClick={() => { setMarkedForRemoval(new Set()); setMatchListOpen(true); }}
            >
              Match List & Schedule Preview
            </Button>
            {hasSchedule && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<PrintIcon />}
                onClick={handlePrintSchedule}
              >
                Print Schedule
              </Button>
            )}
          </Box>
        )}
        <TournamentSchedulePanel
          tournament={tournament}
          onUpdate={fetchTournament}
        />
      </Box>
    );
  };

  const renderScoresTab = () => {
    const allMatches = [...getAllGroupMatches(), ...getAllKnockoutMatches()];
    const timeSlots = getMatchesBySlot(allMatches);
    const totalUnscored = allMatches.filter(m => !m.isScored).length;

    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Typography variant="h6">{totalUnscored > 0 ? `${totalUnscored} matches remaining` : "All matches scored ✓"}</Typography>
          <Button variant="outlined" color="secondary" startIcon={<CasinoIcon />} onClick={() => setRandomDialog(true)} disabled={randomRunning || totalUnscored === 0}>
            {randomRunning ? "Scoring…" : "Random Score All"}
          </Button>
        </Box>

        {timeSlots.length === 0 && (
          <Alert severity="info">No matches yet. Generate groups first, then score matches here.</Alert>
        )}

        <Stack spacing={1}>
          {timeSlots.map(({ slot, label, matches: slotMatches }) => {
            const allScored = slotMatches.every(m => m.isScored);
            const scoredCount = slotMatches.filter(m => m.isScored).length;
            const expanded = slotOverrides.has(slot) ? slotOverrides.get(slot)! : !allScored;
            const toggle = () => setSlotOverrides(prev => { const n = new Map(prev); n.set(slot, !expanded); return n; });

            return (
              <Accordion key={slot} expanded={expanded} onChange={toggle} disableGutters sx={{ border: 1, borderColor: "divider", borderRadius: "8px !important", "&:before": { display: "none" } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                    <TimeIcon fontSize="small" color={allScored ? "disabled" : "primary"} />
                    <Typography fontWeight={700} sx={{ fontSize: "0.95rem" }}>{label}</Typography>
                    <Chip label={`${scoredCount}/${slotMatches.length}`} size="small" color={allScored ? "success" : "default"} variant={allScored ? "filled" : "outlined"} sx={{ ml: "auto", mr: 1 }} />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
                    {slotMatches.map(match => {
                      const key = match.stage === "knockout" ? `knockout-${match.matchId}` : `${match.groupId}-${match.team1Id}-${match.team2Id}`;
                      const isEditing = editingScores.has(key);
                      let saved = { team1Games: 0, team2Games: 0 };
                      if (match.stage === "group") {
                        const g = tournament?.groups?.find(gr => gr.id === match.groupId);
                        const r = g?.matchResults?.find((r: any) => (r.team1Id === match.team1Id && r.team2Id === match.team2Id) || (r.team1Id === match.team2Id && r.team2Id === match.team1Id));
                        if (r) saved = { team1Games: r.team1Id === match.team1Id ? r.team1Games : r.team2Games, team2Games: r.team1Id === match.team1Id ? r.team2Games : r.team1Games };
                      } else {
                        saved = { team1Games: match.team1Games, team2Games: match.team2Games };
                      }
                      const display = match.isScored && !isEditing ? saved : (matchScores[key] || { team1Games: 0, team2Games: 0 });
                      const showInputs = !match.isScored || isEditing;
                      const t1 = tournament?.registeredTeams?.find((t: any) => t.id === match.team1Id);
                      const t2 = tournament?.registeredTeams?.find((t: any) => t.id === match.team2Id);
                      const p = (t: any) => players.find((p: Player) => p.id === t?.player1Id);
                      const q = (t: any) => players.find((p: Player) => p.id === t?.player2Id);
                      const t1n = t1?.teamName || `${p(t1)?.name || "P1"} & ${q(t1)?.name || "P2"}`;
                      const t2n = t2?.teamName || `${p(t2)?.name || "P1"} & ${q(t2)?.name || "P2"}`;
                      const fmtI = getFormatInfo(tournament!, match.stage);
                      const isCancelled = !!match.isCancelled;
                      return (
                        <Box key={key} sx={{ px: 2, py: 1, bgcolor: match.isScored ? "action.hover" : "background.paper", opacity: isCancelled ? 0.6 : 1, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                          <Chip label={match.courtNumber ? `Ct ${match.courtNumber}` : "—"} size="small" variant="outlined" sx={{ minWidth: 52, fontWeight: 600 }} />
                          <Box sx={{ flex: 1, minWidth: 100 }}>
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ textDecoration: isCancelled ? "line-through" : "none" }}>{t1n}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>{p(t1)?.name} & {q(t1)?.name}</Typography>
                          </Box>
                          {isCancelled ? (
                            <Chip label="Cancelled" size="small" color="default" sx={{ minWidth: 90 }} />
                          ) : (
                            <Stack spacing={0.25} alignItems="center">
                              {!match.isScored && <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>{fmtI.label} · to {fmtI.target}</Typography>}
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <TextField type="number" value={display.team1Games} onChange={e => updateMatchScore(key, 1, parseInt(e.target.value) || 0)} inputProps={{ min: 0, style: { textAlign: "center", padding: "4px 6px" } }} sx={{ width: 52, "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none" }, "& input[type=number]": { MozAppearance: "textfield" } }} size="small" disabled={!showInputs} />
                                <Typography variant="body2" color="text.secondary">–</Typography>
                                <TextField type="number" value={display.team2Games} onChange={e => updateMatchScore(key, 2, parseInt(e.target.value) || 0)} inputProps={{ min: 0, style: { textAlign: "center", padding: "4px 6px" } }} sx={{ width: 52, "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none" }, "& input[type=number]": { MozAppearance: "textfield" } }} size="small" disabled={!showInputs} />
                              </Stack>
                            </Stack>
                          )}
                          <Box sx={{ flex: 1, minWidth: 100 }}>
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ textDecoration: isCancelled ? "line-through" : "none" }}>{t2n}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>{p(t2)?.name} & {q(t2)?.name}</Typography>
                          </Box>
                          {!isCancelled && (showInputs ? (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Button variant="contained" size="small" onClick={() => handleScoreMatch(match)} disabled={display.team1Games === 0 && display.team2Games === 0} sx={{ minWidth: 72 }}>{isEditing ? "Save" : "Submit"}</Button>
                              {isEditing && (
                                <Tooltip title="Cancel edit">
                                  <IconButton size="small" onClick={() => cancelEditingScore(key)}><HighlightOffIcon fontSize="small" /></IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Chip label={`${display.team1Games}–${display.team2Games}`} size="small" color="success" sx={{ minWidth: 52 }} />
                              <Tooltip title="Edit score">
                                <IconButton size="small" onClick={() => startEditingScore(key, saved)}><EditIcon fontSize="small" /></IconButton>
                              </Tooltip>
                            </Stack>
                          ))}
                          <Chip label={match.stage === "knockout" ? `${match.bracketType} · ${String(match.round).replace(/-/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())}` : match.groupName} size="small" color={match.stage === "knockout" ? "secondary" : "primary"} variant="outlined" sx={{ minWidth: 60 }} />
                        </Box>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>

        <Dialog open={randomDialog} onClose={() => setRandomDialog(false)}>
          <DialogTitle>Random score all unscored matches?</DialogTitle>
          <DialogContent><Typography>This assigns random scores to {[...getAllGroupMatches(), ...getAllKnockoutMatches()].filter(m => !m.isScored).length} unscored matches. Cannot be easily undone.</Typography></DialogContent>
          <DialogActions>
            <Button onClick={() => setRandomDialog(false)}>Cancel</Button>
            <Button variant="contained" color="secondary" onClick={handleRandomScoreAll}>Yes, proceed</Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  const renderBracketTab = () => {
    if (!tournament) return null;
    const bracketNames = getBracketNamesForOrg();
    const mastersCup = tournament.knockoutBracket?.mastersCup || (tournament as any).mastersCupBracket || [];
    const tourCup = tournament.knockoutBracket?.tourCup || (tournament as any).tourCupBracket || [];
    const hasBracket = mastersCup.length > 0 || tourCup.length > 0;
    const isDirectBracket = tournament.format === "single-elimination";
    const reg = tournament.registeredTeams?.length || 0;
    const groupsDone = isDirectBracket ? reg >= tournament.minTeams : areAllGroupMatchesComplete(tournament);
    const generateHandler = isDirectBracket ? handleGenerateBracket : handleGenerateKnockout;
    const notReadyMessage = isDirectBracket
      ? `Need at least ${tournament.minTeams} registered teams (have ${reg})`
      : "Complete all group stage matches first";

    const scoreMatch = bracketScoreMatch?.match;
    const scoreTeam1 = scoreMatch ? getTeamName(scoreMatch.team1Id) : "";
    const scoreTeam2 = scoreMatch ? getTeamName(scoreMatch.team2Id) : "";

    return (
      <>
      <Box sx={{ p: 3 }}>
        {/* Generate bracket CTA */}
        {!hasBracket ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <BracketIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
            <Typography variant="h5" gutterBottom>Generate Knockout Bracket</Typography>
            <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 480, mx: "auto" }}>
              {isDirectBracket
                ? "Seed the registered teams directly into a single-elimination bracket."
                : "Once all group stage matches are completed, generate the knockout bracket to start the elimination rounds."}
            </Typography>
            {!groupsDone && <Alert severity="warning" sx={{ mb: 3, maxWidth: 420, mx: "auto" }}>{notReadyMessage}</Alert>}
            <Tooltip title={!groupsDone ? notReadyMessage : ""}>
              <span>
                <Button variant="contained" size="large" startIcon={generatingBracket ? <CircularProgress size={18} color="inherit" /> : <BracketIcon />} onClick={() => generateHandler()} disabled={generatingBracket || !groupsDone}>
                  {generatingBracket ? "Generating…" : "Generate Knockout Bracket"}
                </Button>
              </span>
            </Tooltip>
          </Box>
        ) : (
          <Stack spacing={3}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <ToggleButtonGroup value={bracketMode} exclusive onChange={(_, v) => v && setBracketMode(v)} size="small">
                  <ToggleButton value="mirror"><MirrorIcon sx={{ mr: 0.5 }} fontSize="small" />Mirror</ToggleButton>
                  <ToggleButton value="tree"><TreeIcon sx={{ mr: 0.5 }} fontSize="small" />Tree</ToggleButton>
                </ToggleButtonGroup>
                <Button variant="outlined" size="small" onClick={() => generateHandler()} disabled={generatingBracket}>Regenerate</Button>
                {isDirectBracket && (
                  <Button variant="outlined" size="small" color="warning" onClick={() => handleResetBracket()} disabled={generatingBracket || resettingGroups}>
                    {resettingGroups ? "Resetting..." : "Reset Bracket"}
                  </Button>
                )}
              </Box>
              {areAllKnockoutMatchesScored(tournament) && tournament.status !== "completed" && (
                <Button variant="contained" color="success" onClick={handleCompleteTournament}>Mark as Completed →</Button>
              )}
            </Box>
            {mastersCup.length > 0 && renderBracketSection(withByeCards(mastersCup), isDirectBracket ? "Bracket" : bracketNames.primary, "mastersCup")}
            {tourCup.length > 0 && mastersCup.length > 0 && <Divider />}
            {tourCup.length > 0 && renderBracketSection(withByeCards(tourCup), bracketNames.secondary, "tourCup")}
          </Stack>
        )}
      </Box>

      <Dialog open={!!bracketScoreMatch} onClose={() => !bracketScoreSubmitting && setBracketScoreMatch(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Enter Score</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ flex: 1 }} noWrap>{scoreTeam1}</Typography>
              <TextField
                type="number"
                value={bracketScoreValues.team1Games}
                onChange={e => setBracketScoreValues(v => ({ ...v, team1Games: parseInt(e.target.value) || 0 }))}
                inputProps={{ min: 0, style: { textAlign: "center" } }}
                sx={{ width: 72 }}
                size="small"
                autoFocus
              />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ flex: 1 }} noWrap>{scoreTeam2}</Typography>
              <TextField
                type="number"
                value={bracketScoreValues.team2Games}
                onChange={e => setBracketScoreValues(v => ({ ...v, team2Games: parseInt(e.target.value) || 0 }))}
                inputProps={{ min: 0, style: { textAlign: "center" } }}
                sx={{ width: 72 }}
                size="small"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBracketScoreMatch(null)} disabled={bracketScoreSubmitting}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitBracketScore} disabled={bracketScoreSubmitting}>
            {bracketScoreSubmitting ? "Saving…" : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
      <AdvancementTieDialog
        open={!!advancementTies}
        ties={advancementTies || []}
        getTeamName={getTeamName}
        submitting={generatingBracket}
        onCancel={() => setAdvancementTies(null)}
        onConfirm={(manualSelections) => handleGenerateKnockout(manualSelections)}
      />
      </>
    );
  };

  const renderResultsTab = () => {
    if (!tournament) return null;
    return (
      <Box sx={{ p: 3 }}>
        {/* Group standings */}
        {tournament.groups && tournament.groups.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>Group Standings</Typography>
            <Grid container spacing={2}>
              {tournament.groups.map(g => {
                const rows = g.standings && g.standings.length > 0
                  ? [...g.standings].sort((a, b) => b.points !== a.points ? b.points - a.points : b.gamesDiff !== a.gamesDiff ? b.gamesDiff - a.gamesDiff : b.gamesWon - a.gamesWon)
                  : g.teamIds.map(id => ({ teamId: id, wins: 0, draws: 0, losses: 0, points: 0, gamesWon: 0, gamesLost: 0, gamesDiff: 0, matchesPlayed: 0 }));
                return (
                  <Grid item xs={12} sm={6} md={4} key={g.id}>
                    <Paper variant="outlined">
                      <Box sx={{ p: 1.5, bgcolor: "primary.main", borderRadius: "3px 3px 0 0" }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#fff" }}>{g.name}</Typography>
                      </Box>
                      <Box>
                        {rows.map((s, i) => (
                          <Box key={s.teamId} sx={{ display: "flex", alignItems: "center", px: 1.5, py: 0.75, borderBottom: i < rows.length - 1 ? 1 : 0, borderColor: "divider" }}>
                            <Typography variant="caption" fontWeight={700} color="primary" sx={{ minWidth: 20 }}>{i + 1}</Typography>
                            <Typography variant="body2" sx={{ flex: 1 }}>{getTeamName(s.teamId)}</Typography>
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                              <Chip label={`${s.wins}W`} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.6rem" }} />
                              <Chip label={`${s.losses}L`} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.6rem" }} />
                              <Chip label={`GD ${s.gamesDiff > 0 ? "+" : ""}${s.gamesDiff}`} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.6rem" }} />
                              <Chip label={`${s.points}pts`} size="small" color="primary" sx={{ height: 18, fontSize: "0.6rem" }} />
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* Final rankings */}
        {tournament.status === "completed" && (
          <Box>
            <Typography variant="h6" gutterBottom>🏆 Final Rankings</Typography>
            <Button variant="outlined" startIcon={<TrophyIcon />} onClick={async () => {
              setRankingsLoading(true);
              try {
                const r = await apiRequest(`/api/tournaments/${tournament.id}/rankings`);
                setRankings(await r.json());
              } finally { setRankingsLoading(false); }
            }}>
              {rankingsLoading ? "Loading…" : "Load Final Rankings"}
            </Button>
            {rankings && (() => {
              const bracketNames = getBracketNamesForOrg();
              const sections: Array<{ label: string; entries: [string, string[]][] }> = [];
              if (rankings.mastersCup) sections.push({ label: bracketNames.primary, entries: Object.entries(rankings.mastersCup) as [string, string[]][] });
              if (rankings.tourCup) sections.push({ label: bracketNames.secondary, entries: Object.entries(rankings.tourCup) as [string, string[]][] });
              return (
                <Stack spacing={3} sx={{ mt: 2, maxWidth: 400 }}>
                  {sections.map(section => (
                    <Box key={section.label}>
                      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>{section.label}</Typography>
                      <Stack spacing={1}>
                        {section.entries.sort((a, b) => parseInt(a[0]) - parseInt(b[0])).map(([pos, teamIds]) => {
                          const posNum = parseInt(pos);
                          const tied = teamIds.length > 1;
                          const posLabel = tied ? `${posNum}/${posNum + teamIds.length - 1}` : pos;
                          return (
                            <Box key={pos} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, border: 1, borderColor: posNum <= 3 ? "warning.light" : "divider", borderRadius: 1, bgcolor: posNum <= 3 ? "warning.50" : "transparent" }}>
                              <Typography variant="h6" fontWeight={700} sx={{ minWidth: 48 }}>#{posLabel}</Typography>
                              <TrophyIcon sx={{ color: posNum === 1 ? "#FFD700" : posNum === 2 ? "#C0C0C0" : posNum === 3 ? "#CD7F32" : "text.disabled", fontSize: 20 }} />
                              <Typography variant="body1" fontWeight={600}>{teamIds.map(id => getTeamName(id)).join("  /  ")}</Typography>
                              {tied && <Typography variant="caption" color="text.secondary">(tied — cancelled)</Typography>}
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              );
            })()}
          </Box>
        )}

        {tournament.status !== "completed" && (!tournament.groups || !tournament.groups.some(g => g.standings?.length)) && (
          <Alert severity="info">Results will appear here as matches are scored.</Alert>
        )}
      </Box>
    );
  };

  // ─── main render ─────────────────────────────────────────────────────────

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}><CircularProgress /></Box>;
  if (!tournament) return <Box sx={{ p: 3 }}><Alert severity="error">Tournament not found.</Alert></Box>;

  const reg = tournament.registeredTeams?.length || 0;
  const unscored = [...getAllGroupMatches(), ...getAllKnockoutMatches()].filter(m => !m.isScored).length;
  const hasBracket = !!(tournament.knockoutBracket?.mastersCup?.length || tournament.knockoutBracket?.tourCup?.length || (tournament as any).mastersCupBracket?.length);
  const hasGroups = (tournament.groups?.length || 0) > 0;
  const hasGroupStage = tournament.format !== "single-elimination";
  const hasStage = hasGroups || hasBracket;

  // Built conditionally (rather than filtered) so tab labels and their panels below
  // stay index-aligned regardless of which tabs are included for this format.
  const tabDefs: { label: string; badge?: string; badgeColor?: string; render: () => React.ReactNode }[] = [
    { label: "Overview", render: renderOverviewTab },
    { label: "Payment", render: renderPaymentTab },
    { label: "Email", render: renderEmailTab },
    { label: "Gifts", render: renderGiftsTab },
    { label: "Teams", badge: reg > 0 ? String(reg) : undefined, render: renderTeamsTab },
    { label: "Player Info", badge: undefined, render: renderPlayerInfoTab },
    ...(hasGroupStage ? [{ label: "Groups", badge: hasGroups ? String(tournament.groups?.length || 0) : undefined, badgeColor: hasGroups ? "primary" : undefined, render: renderGroupsTab }] : []),
    { label: "Schedule", badge: hasStage ? "✓" : undefined, badgeColor: hasStage ? "success" : undefined, render: renderScheduleTab },
    { label: "Scores", badge: unscored > 0 ? String(unscored) : (hasStage ? "✓" : undefined), badgeColor: unscored > 0 ? "warning" : "success", render: renderScoresTab },
    { label: "Bracket", badge: hasBracket ? "✓" : undefined, badgeColor: "success", render: renderBracketTab },
    { label: "Results", render: renderResultsTab },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Header */}
      <Box
        sx={{
          ...(currentOrg?.slug === "padel-eagle"
            ? { backgroundColor: "#743444" }
            : { backgroundImage: (tournament as any).posterImageUrl
              ? `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${(tournament as any).posterImageUrl})`
              : "linear-gradient(135deg, #1a237e 0%, #283593 60%, #3949ab 100%)" }),
          backgroundSize: "cover", backgroundPosition: "center",
          px: 3, py: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <IconButton onClick={() => navigate("/admin/tournaments")} sx={{ color: "#fff" }} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>Tournaments</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          <Box>
            <Typography variant="h4" sx={{ color: "#fff", fontWeight: 800 }}>{tournament.name}</Typography>
            <Box sx={{ display: "flex", gap: 0.75, mt: 0.75 }}>
              <Chip label={getStatusLabel(tournament.status)} color={getStatusColor(tournament.status)} size="small" sx={{ fontWeight: 700 }} />
              <Chip label={tournament.level} size="small" sx={{ bgcolor: "rgba(255,255,255,0.85)", fontWeight: 600 }} />
              <Chip label={`📅 ${new Date(tournament.startDate).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`} size="small" sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "#fff" }} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper", px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 0 } }}>
        {isMobile ? (
          <FormControl fullWidth size="small">
            <InputLabel id="tournament-admin-tab-select-label">Section</InputLabel>
            <Select
              labelId="tournament-admin-tab-select-label"
              value={tab}
              label="Section"
              onChange={(e) => setTab(Number(e.target.value))}
            >
              {tabDefs.map((t, i) => (
                <MenuItem key={t.label} value={i}>
                  {t.label}{t.badge ? ` (${t.badge})` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              width: "100%",
              maxWidth: "100%",
              minHeight: 48,
              overflow: "hidden",
              "& .MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.35 },
              "& .MuiTabs-scroller": {
                overflow: "hidden !important",
              },
              "& .MuiTab-root": {
                minHeight: 48,
                minWidth: 96,
                px: 1.5,
                fontSize: "0.875rem",
              },
            }}
          >
            {tabDefs.map((t, i) => (
              <Tab
                key={i}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {t.label}
                    {t.badge && <Chip label={t.badge} size="small" color={(t.badgeColor as any) || "default"} sx={{ height: 18, fontSize: "0.6rem", minWidth: 20 }} />}
                  </Box>
                }
              />
            ))}
          </Tabs>
        )}
      </Box>

      {/* Snackbar */}
      {snack && (
        <Alert severity="info" onClose={() => setSnack("")} sx={{ mx: 3, mt: 2 }}>{snack}</Alert>
      )}

      {/* Display Mode Selector Dialog */}
      <Dialog
        open={displayModeDialogOpen}
        onClose={() => setDisplayModeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Choose Display Mode for Live View</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Card
              sx={{ cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
              onClick={() => handleOpenLiveDisplay("slideshow")}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>📽️ Auto-Cycling Slideshow</Typography>
                <Typography variant="body2" color="text.secondary">
                  Automatically cycles through all sections. Choose custom timing in the next step.
                </Typography>
              </CardContent>
            </Card>

            <Card
              sx={{ cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
              onClick={() => handleOpenLiveDisplay("scroll")}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>📜 Single Scrolling Page</Typography>
                <Typography variant="body2" color="text.secondary">
                  All information visible on one long page. Scroll to see different sections.
                </Typography>
              </CardContent>
            </Card>

            <Card
              sx={{ cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
              onClick={() => handleOpenLiveDisplay("tabs")}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>🗂️ Tabbed View</Typography>
                <Typography variant="body2" color="text.secondary">
                  Manual navigation with tabs. Click tabs to switch between sections.
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisplayModeDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Slide Time Selection Dialog */}
      <Dialog
        open={slideTimeDialogOpen}
        onClose={() => {
          setSlideTimeDialogOpen(false);
          setSlideConfig(defaultSlideConfig());
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>⏱️ Configure Slideshow</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which slides to show and for how long each is displayed.
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", px: 1, pb: 0.5, borderBottom: 1, borderColor: "divider", mb: 1 }}>
            <Typography variant="caption" fontWeight={700} sx={{ flex: 1 }}>Slide</Typography>
            <Typography variant="caption" fontWeight={700} sx={{ width: 60, textAlign: "center" }}>Show</Typography>
            <Typography variant="caption" fontWeight={700} sx={{ width: 140, textAlign: "center" }}>Duration</Typography>
          </Box>

          <Stack spacing={0.5}>
            {SLIDE_DEFS.map((slide) => {
              const cfg = slideConfig[slide.key] || { enabled: true, duration: 15 };
              return (
                <Box key={slide.key} sx={{ display: "flex", alignItems: "center", px: 1, py: 0.5, borderRadius: 1, bgcolor: cfg.enabled ? "action.selected" : "transparent" }}>
                  <Typography variant="body2" sx={{ flex: 1, color: cfg.enabled ? "text.primary" : "text.disabled" }}>
                    {slide.label}
                  </Typography>
                  <Box sx={{ width: 60, display: "flex", justifyContent: "center" }}>
                    <Checkbox
                      checked={cfg.enabled}
                      onChange={(e) =>
                        setSlideConfig((prev) => ({
                          ...prev,
                          [slide.key]: { ...prev[slide.key], enabled: e.target.checked },
                        }))
                      }
                      size="small"
                    />
                  </Box>
                  <Box sx={{ width: 140, display: "flex", justifyContent: "center" }}>
                    <Select
                      value={cfg.duration}
                      onChange={(e) =>
                        setSlideConfig((prev) => ({
                          ...prev,
                          [slide.key]: { ...prev[slide.key], duration: Number(e.target.value) },
                        }))
                      }
                      size="small"
                      disabled={!cfg.enabled}
                      sx={{ width: 130, fontSize: "0.8rem" }}
                    >
                      {[5, 10, 15, 20, 30, 45, 60].map((s) => (
                        <MenuItem key={s} value={s}>{s}s</MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>
              );
            })}
          </Stack>

          {SLIDE_DEFS.filter((s) => slideConfig[s.key]?.enabled).length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Select at least one slide to show.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSlideTimeDialogOpen(false);
              setSlideConfig(defaultSlideConfig());
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleOpenSlideshow}
            variant="contained"
            disabled={SLIDE_DEFS.filter((s) => slideConfig[s.key]?.enabled).length === 0}
          >
            Open Slideshow
          </Button>
        </DialogActions>
      </Dialog>

      {/* Match List & Schedule Preview Dialog — always mounted so it works from any tab */}
      {tournament && (() => {
        const matchDuration = tournament.timedMatchDuration || 20;
        const teams = tournament.registeredTeams || [];
        const getTeamLabel = (id?: string) => {
          const t = teams.find(t => t.id === id);
          if (!t) return id || "?";
          return t.teamName || `${t.player1Name || "?"} & ${t.player2Name || "?"}`;  
        };

        const groupMatches: { key: string; groupName: string; label: string; hasTime: boolean; time?: string; isWalkover?: boolean }[] = [];
        (tournament.groups || []).forEach((g, gi) => {
          (g.matchResults || []).forEach((m, mi) => {
            groupMatches.push({
              key: `group-${gi}-${mi}`,
              groupName: g.name,
              label: `${getTeamLabel(m.team1Id)} vs ${getTeamLabel(m.team2Id)}`,
              hasTime: !!m.startTime,
              time: m.startTime ? new Date(m.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) + (m.courtNumber ? ` · Court ${m.courtNumber}` : "") : undefined,
              isWalkover: (m as any).isWalkover,
            });
          });
        });

        const groupsByName = groupMatches.reduce<Record<string, typeof groupMatches>>((acc, m) => {
          (acc[m.groupName] = acc[m.groupName] || []).push(m);
          return acc;
        }, {});

        const ROUND_LABELS: Record<string, string> = {
          "round-of-32": "Round of 32", "round-of-16": "Round of 16",
          "quarter-finals": "Quarter-Finals", "semi-finals": "Semi-Finals",
          "final": "Final", "consolation-semi-finals": "Consolation Semi-Finals",
          "third-place": "Third Place", "fifth-place": "Fifth Place", "seventh-place": "Seventh Place",
          "Round 1": "Round 1", "Round 2": "Round 2", "Round 3": "Round 3", "Round 4": "Round 4",
        };
        const knockoutMatches: { key: string; round: string; bracketLabel: string; label: string; hasTime: boolean; time?: string; isWalkover?: boolean }[] = [];
        const ko = tournament.knockoutBracket || {};
        const bNames = getBracketNamesForOrg();
        (["mastersCup", "tourCup"] as const).forEach(bType => {
          const bracketLabel = bType === "mastersCup" ? bNames.primary : bNames.secondary;
          (ko[bType] || []).forEach((m, mi) => {
            knockoutMatches.push({
              key: `knockout-${bType}-${mi}`,
              round: m.round,
              bracketLabel,
              label: m.team1Id && m.team2Id
                ? `${getTeamLabel(m.team1Id)} vs ${getTeamLabel(m.team2Id)}`
                : (m.team1Placeholder && m.team2Placeholder
                  ? `${m.team1Placeholder} vs ${m.team2Placeholder}`
                  : `Match #${m.matchNumber}`),
              hasTime: !!m.startTime,
              time: m.startTime ? new Date(m.startTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) + (m.courtNumber ? ` · Court ${m.courtNumber}` : "") : undefined,
              isWalkover: (m as any).isWalkover,
            });
          });
        });

        const knockoutByRound = knockoutMatches.reduce<Record<string, typeof knockoutMatches>>((acc, m) => {
          const key = `${m.bracketLabel} – ${ROUND_LABELS[m.round] || m.round}`;
          (acc[key] = acc[key] || []).push(m);
          return acc;
        }, {});

        const markedCount = markedForRemoval.size;
        const timeSavedMin = markedCount * matchDuration;
        const totalCount = groupMatches.length + knockoutMatches.length;

        const toggleMark = (key: string) => {
          setMarkedForRemoval(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
          });
        };

        return (
          <Dialog open={matchListOpen} onClose={() => { setMatchListOpen(false); setMarkedForRemoval(new Set()); }} maxWidth="md" fullWidth>
            <DialogTitle>Match List & Schedule Preview</DialogTitle>
            <DialogContent dividers>
              {markedCount > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>{markedCount} match{markedCount !== 1 ? "es" : ""} marked for removal</strong>
                  {" — "}
                  Time saved: <strong>{timeSavedMin} min</strong> ({markedCount} × {matchDuration} min)
                  {" · "}
                  {markedCount} court slot{markedCount !== 1 ? "s" : ""} freed
                </Alert>
              )}
              {totalCount === 0 && (
                <Alert severity="warning">No matches yet. Generate groups and auto-schedule first.</Alert>
              )}

              {Object.entries(groupsByName).length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Group Stage</Typography>
                  <Stack spacing={1}>
                    {Object.entries(groupsByName).map(([groupName, entries]) => (
                      <Box key={groupName}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>{groupName}</Typography>
                        <Stack spacing={0.5}>
                          {entries.map((entry, i) => {
                            const isMarked = markedForRemoval.has(entry.key);
                            return (
                              <Paper key={entry.key} variant="outlined" sx={{ p: 1, display: "flex", alignItems: "center", gap: 1, opacity: isMarked ? 0.55 : 1, bgcolor: isMarked ? "action.selected" : "transparent" }}>
                                <Checkbox size="small" checked={isMarked} onChange={() => toggleMark(entry.key)} />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ textDecoration: isMarked ? "line-through" : "none" }}>
                                    Match {i + 1}: {entry.label}
                                  </Typography>
                                  {entry.time && <Typography variant="caption" color="text.secondary">{entry.time}</Typography>}
                                  {entry.isWalkover && <Typography variant="caption" color="text.secondary">Walkover — Bye (no match played)</Typography>}
                                  {!entry.hasTime && !entry.isWalkover && <Typography variant="caption" color="warning.main">Not yet scheduled</Typography>}
                                </Box>
                                {isMarked && <Chip label="Removed" size="small" color="error" sx={{ height: 18, fontSize: 10 }} />}
                              </Paper>
                            );
                          })}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {Object.entries(knockoutByRound).length > 0 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Knockout Stage</Typography>
                  <Stack spacing={1}>
                    {Object.entries(knockoutByRound).map(([roundKey, entries]) => (
                      <Box key={roundKey}>
                        <Typography variant="subtitle2" color="secondary" sx={{ mb: 0.5 }}>{roundKey}</Typography>
                        <Stack spacing={0.5}>
                          {entries.map((entry) => {
                            const isMarked = markedForRemoval.has(entry.key);
                            return (
                              <Paper key={entry.key} variant="outlined" sx={{ p: 1, display: "flex", alignItems: "center", gap: 1, opacity: isMarked ? 0.55 : 1, bgcolor: isMarked ? "action.selected" : "transparent" }}>
                                <Checkbox size="small" checked={isMarked} onChange={() => toggleMark(entry.key)} />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ textDecoration: isMarked ? "line-through" : "none" }}>{entry.label}</Typography>
                                  {entry.time && <Typography variant="caption" color="text.secondary">{entry.time}</Typography>}
                                  {entry.isWalkover && <Typography variant="caption" color="text.secondary">Walkover — Bye (no match played)</Typography>}
                                  {!entry.hasTime && !entry.isWalkover && <Typography variant="caption" color="warning.main">Not yet scheduled</Typography>}
                                </Box>
                                {isMarked && <Chip label="Removed" size="small" color="error" sx={{ height: 18, fontSize: 10 }} />}
                              </Paper>
                            );
                          })}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              {markedCount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1, px: 1 }}>
                  Removing {markedCount}/{totalCount} matches · {timeSavedMin} min saved · {markedCount} court slot{markedCount !== 1 ? "s" : ""} freed
                </Typography>
              )}
              <Button onClick={() => setMarkedForRemoval(new Set())} disabled={markedCount === 0}>Clear selection</Button>
              <Button onClick={() => { setMatchListOpen(false); setMarkedForRemoval(new Set()); }}>Close</Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Group draw dialogs — always mounted so they can open from Groups tab */}
      <Dialog open={potDialogOpen} onClose={() => setPotDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Group Draw – Pot Management</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="info">
              Teams are divided into pots based on average level. Pot 1 contains the strongest teams. You can move teams between pots before starting the draw.
            </Alert>
            <Grid container spacing={2}>
              {pots.map((pot) => (
                <Grid item xs={12} md={pots.length === 3 ? 4 : 3} key={pot.potNumber}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">Pot {pot.potNumber}</Typography>
                      <Divider sx={{ mb: 2 }} />
                      <List dense>
                        {pot.teams.map((team) => (
                          <ListItem key={team.id} sx={{ border: 1, borderColor: "divider", borderRadius: 1, mb: 1, flexDirection: "column", alignItems: "flex-start", p: 1 }}>
                            <Typography variant="body2" fontWeight="bold">{team.name}</Typography>
                            <Typography variant="caption" color="text.secondary">Avg Level: {team.avgLevel.toFixed(1)}</Typography>
                            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                              {pot.potNumber > 1 && (
                                <Button size="small" variant="outlined" onClick={() => moveTeamBetweenPots(team.id, pot.potNumber, pot.potNumber - 1)}>↑ Pot {pot.potNumber - 1}</Button>
                              )}
                              {pot.potNumber < pots.length && (
                                <Button size="small" variant="outlined" onClick={() => moveTeamBetweenPots(team.id, pot.potNumber, pot.potNumber + 1)}>↓ Pot {pot.potNumber + 1}</Button>
                              )}
                            </Stack>
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPotDialogOpen(false)}>Cancel</Button>
          <Button onClick={startDraw} variant="contained" startIcon={<TrophyIcon />}>Start Draw Animation</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={drawAnimationOpen} onClose={() => !isDrawing && setDrawAnimationOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Group Stage Draw</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {isDrawing && (
              <Alert severity="info">Drawing teams… Step {currentDrawStep + 1} of {pots.length * drawGroups.length}</Alert>
            )}
            {!isDrawing && <Alert severity="success">Draw complete! Review the groups and click "Finalize" to save.</Alert>}
            {!isDrawing && <Alert severity="info">💡 You can drag and drop teams between groups to adjust the draw before finalizing.</Alert>}
            <Grid container spacing={2}>
              {drawGroups.map((group, idx) => (
                <Grid item xs={12} md={drawGroups.length <= 4 ? 6 : 4} key={idx}>
                  <Card
                    variant="outlined"
                    sx={{ bgcolor: "background.default", border: draggedTeam && !isDrawing ? "2px dashed" : "1px solid", borderColor: draggedTeam && !isDrawing ? "primary.main" : "divider" }}
                    onDragOver={!isDrawing ? handleDrawDragOver : undefined}
                    onDrop={!isDrawing ? () => handleDrawDrop(idx) : undefined}
                  >
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">{group.name}</Typography>
                      <Divider sx={{ mb: 2 }} />
                      <List dense>
                        {group.teams.map((team, teamIdx) => (
                          <ListItem
                            key={team.id}
                            draggable={!isDrawing}
                            onDragStart={!isDrawing ? () => handleDrawDragStart(team, idx) : undefined}
                            sx={{
                              border: 1, borderColor: "primary.main", borderRadius: 1, mb: 1,
                              bgcolor: "background.paper", cursor: !isDrawing ? "grab" : "default",
                              opacity: draggedTeam?.team.id === team.id ? 0.5 : 1,
                              animation: "fadeIn 0.5s ease-in",
                              "@keyframes fadeIn": { from: { opacity: 0, transform: "translateY(-10px)" }, to: { opacity: 1, transform: "translateY(0)" } },
                            }}
                          >
                            <ListItemText
                              primary={team.name}
                              secondary={`Avg Level: ${team.avgLevel.toFixed(1)} • From Pot ${teamIdx + 1}`}
                              primaryTypographyProps={{ fontWeight: "bold" }}
                            />
                          </ListItem>
                        ))}
                        {group.teams.length === 0 && (
                          <Typography variant="body2" color="text.secondary" align="center">
                            {isDrawing ? "Waiting for draw…" : "Drop team here"}
                          </Typography>
                        )}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {isDrawing && <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}><LinearProgress sx={{ width: "50%" }} /></Box>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDrawAnimationOpen(false)} disabled={isDrawing}>Cancel</Button>
          {isDrawing && (
            <Button onClick={skipAnimation} variant="outlined" color="secondary">
              Skip Animation
            </Button>
          )}
          <Button onClick={finalizeDrawGroups} variant="contained" disabled={isDrawing || generating}>
            {generating ? "Saving…" : "Finalize Groups"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tab content */}
      {tabDefs[tab]?.render()}
    </Box>
  );
}
